import React, {
  Dispatch,
  DragEventHandler,
  ElementType,
  FormEventHandler,
  MouseEventHandler,
  ReactElement,
  SetStateAction,
  FunctionComponent,
} from 'react';
import { EmailStyleGuide } from './components/application/conversational_search_config/types';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { GlobalReducerAction } from './components/application/smart-forms/builder/globalReducer';
import { IntegrationType } from './components/application/integrations/types';
import ColorHexCode from './utils/ColorHexCode';
import { GeneralHash, ThirdPartyAnalyticsImplementationType } from './widgets/global-embed-scripts/types';
import iconMap from './utils/icon-map';
import { ConditionalLogicConfig } from './components/application/smart-forms/builder/screens/conditional-logic/types';
import { AttributeFilterExpression } from './utils/attributeFilterExpression';
export * from './widgets/global-embed-scripts/types';

export const DEFAULT_FILTER_START_DATE = '2021-01-01';

export const RATING_THRESHOLD = 5;

export const SIDEBAR_EXPANDED_WIDTH = 256;
export const SIDEBAR_COLLAPSED_WIDTH = 64;
export const SIDEBAR_TRANSITION_DURATION_MS = 300;

export interface PickerItem<T = string> {
  key: T;
  name: string;
  isPublished?: boolean;
  isDiscarded?: boolean;
  isArchived?: boolean;
  isLabel?: boolean;
  iconComponent?: React.FC<any>;
  isDisabled?: boolean;
  tooltipText?: string
}

export interface DescriptivePickerItem<T = string> {
  key: T;
  title: string;
  description: string;
  Icon: any;
}

export interface PhoneNumberRequestFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  title: string;
  employerIdentificationNumber: string;
  registeredBusinessName: string;
  universityAddress: string;
  preferredAreaCode: string;
  secondaryAreaCode: string;
}

export enum TinyMCEKey {
  production = 'cohmrpfn8f6tebwlflyst5br0xopgbfxoenpu0d0c6krpp5q',
}

export enum WebhookRequestMethods {
  GET = 'get',
  POST = 'post',
}

export enum FactsRequestMethods {
  GET = 'get',
  POST = 'post',
}

export enum WebhookTriggers {
  NEW_SMART_FORM_SUBMISSION = 'new_smart_form_submission',
}

export enum WebhookPayloadFormats {
  JSON = 'json',
  URL_ENCODED = 'url_encoded',
}

export interface KeyValuePair {
  key: string;
  value: string | number | null;
  // Secret values never reach the FE; true means the server has a stored
  // value and a blank value on save keeps it.
  hasSavedValue?: boolean;
}

export interface Webhook {
  uid: string;
  name: string;
  trigger: WebhookTriggers;
  requestMethod: WebhookRequestMethods;
  endpoint: string;
  headers: Array<KeyValuePair>;
  queryParams: Array<KeyValuePair>;
  smartFormId: string;
  isActive: boolean;
  exportAllFields: boolean;
  payloadFormat: string;
}

export interface WebhookConnection {
  uid: string;
  name: string;
  requestMethod: WebhookRequestMethods;
  endpoint: string;
  headers: Array<KeyValuePair>;
  queryParams: Array<KeyValuePair>;
  isActive: boolean;
  payloadFormat: string;
}

export interface WebhookImport {
  id: string;
  name: string;
  isActive: boolean;
  token: string;
  funnelStageMappings?: Array<WebhookImportFunnelStageMapping>;
}

export interface WebhookImportFunnelStageMapping {
  id: string;
  externalStageName: string;
  funnelStageId: string;
}

export interface WebhookFieldMapping {
  fieldUid: string;
  smartFormId: string;
  webhookFieldName: string;
}

export enum TriggerType {
  WELCOME = 'WELCOME',
  SCROLL = 'SCROLL',
  SCROLL_UP = 'SCROLL_UP',
  DELAY = 'DELAY',
  SCROLL_PERCENT = 'SCROLL_PERCENT',
  EXIT = 'EXIT',
  CLICK = 'CLICK',
}

export enum ScheduledTaskFrequency {
  MINUTELY = 'MINUTELY',
  HOURLY = 'HOURLY',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum ScheduledTaskOffsetUnit {
  MINUTES = 'MINUTES',
  HOURS = 'HOURS',
  DAYS = 'DAYS',
}

// Positive divisors of 60. Allowed values for ScheduledTask#frequency_value when
// frequency is MINUTELY.
export const MINUTELY_FREQUENCY_VALUES: ReadonlyArray<number> = [
  1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30, 60,
];

export const DEFAULT_MINUTELY_FREQUENCY_VALUE = 5;

export enum ScheduledTaskStatus {
  NEVER_RUN = 'never_run',
  OVERDUE = 'overdue',
  FAILED = 'failed',
  SUCCESS = 'success',
  UNKNOWN = 'unknown',
}

export interface Targeting {
  trigger: TriggerType;
}

export interface TriggerSetting {
  key: TriggerType;
  name: string;
  description: string;
  hasValue: boolean;
  maxValue?: number;
  afterInputText?: string;
}

export enum FrequencyType {
  EVERY_VISIT = 'EVERY_VISIT',
  ONCE = 'ONCE',
  RETURN_VISIT = 'RETURN_VISIT'
}

export enum FeatureFlagNames {
  AGENTIC_HUB = 'is_agentic_hub_enabled',
  AHA_FEEDBACK_SURVEY = 'is_aha_feedback_survey_enabled',
  AI_EMAIL = 'is_ai_email_enabled',
  AI_EMAIL_V2 = 'is_email_dashboard_v2_enabled',
  BEHAVIORAL_SMS_ENABLED = 'is_behavioral_sms_enabled',
  ALLOW_REALTIME_VOICE_MODELS = 'allow_realtime_voice_models',
  AUTO_ADVANCE = 'is_auto_advance_enabled',
  BRAND_POSTURE = 'is_brand_posture_enabled',
  CALL_TRANSFER = 'is_call_transfer_enabled',
  SMS_CALL_HANDOFF = 'is_sms_call_handoff_enabled',
  CLICK_TO_POP_AHA_IN_HUB = 'is_click_to_pop_aha_in_hub_enabled',
  COMEBACK_EMAIL_CONFIG_V3 = 'is_comeback_email_config_v3_enabled',
  COMEBACK_EMAIL_OUTPUTS = 'is_comeback_email_outputs_enabled',
  COMEBACK_SIMULATION = 'is_comeback_simulation_enabled',
  CSV_FORCE_QUOTES = 'is_csv_force_quotes_enabled',
  DLP_RESPONSE_PAGE_IN_HUB = 'is_dlp_response_page_in_hub_enabled',
  DRIP_CAMPAIGN_EMAIL_OUTPUTS = 'is_drip_campaign_email_outputs_enabled',
  DRIP_CAMPAIGN_SIMULATION = 'is_drip_campaign_simulation_enabled',
  EMAIL_PROMPT_CUSTOMIZATION = 'is_email_prompt_customization_enabled',
  ENGAGEMENT_SCORE = 'is_engagement_score_enabled',
  FIELD_DUPLICATE_DETECTION = 'is_field_duplicate_detection_enabled',
  FLE_COLLECTOR = 'is_fle_collector_enabled',
  FORCE_JS_SCRAPER = 'is_force_js_scraper_enabled',
  HALDA_ENGINE = 'is_halda_engine_enabled',
  GEMINI_ENGINE = 'is_gemini_engine_enabled',
  GEMINI_CONFIGURATION = 'is_gemini_configuration_enabled',
  LANDING_PAGE = 'has_landing_page_feature',
  LEAD_INGESTION_ENABLED = 'is_lead_ingestion_enabled',
  NEW_HUB = 'is_new_hub_enabled',
  PERPLEXITY_ENGINE = 'is_perplexity_engine_enabled',
  NEW_HUB_DEMO = 'is_new_hub_demo_enabled',
  PHONE_CALL = 'is_phone_call_enabled',
  RECAPTCHA = 'is_recaptcha_enabled',
  SEGMENTATION_AI_SEARCH = 'is_segmentation_ai_search_enabled',
  SEGMENT_BASED_ROUTING = 'is_segment_based_routing_enabled',
  SEMANTIC_TOPIC_CARD_SEARCH = 'is_semantic_topic_card_search_enabled',
  RAGIE_DOCUMENT_UPLOAD = 'is_ragie_document_upload_enabled',
  SHOW_ALL_DLPS_AT_LEAST_ONCE = 'is_show_all_dlps_at_least_once_enabled',
  SMS = 'is_sms_enabled',
  SMS_CAMPAIGNS = 'is_sms_campaigns_enabled',
  SMS_DATA_CAPTURE = 'is_sms_data_capture_enabled',
  STUDENT_RECORDS_V3 = 'is_student_records_v3_enabled',
  VIEW_AS_STUDENT = 'is_view_as_student_enabled',
  STUDENT_RECRUITER_CONFIG_VISIBLE = 'is_student_recruiter_config_visible',
  STRIP_WEBSITE_BACKGROUND_SCRIPTS = 'is_strip_website_background_scripts_enabled',
  URL_PARAMETER_MATCHING = 'is_url_parameter_matching_enabled',
  WEBSITE_PREVIEW_BACKGROUND = 'is_website_preview_background_enabled',
  WHATSAPP = 'is_whatsapp_enabled',
  USE_SIMPLE_AI_SEARCH_FOR_TOP_PARAGRAPH = 'use_simple_ai_search_for_top_paragraph',
  STATIC_SFTP_FILENAME = 'is_static_sftp_filename_enabled',
  SLATE_WEB_SERVICE_IMPORT = 'is_slate_web_service_import_enabled',
  TRIGGER_TYPE_CAMPAIGN_ID = 'is_trigger_type_campaign_id_enabled',
}

export enum AhaTab {
  CONVERSATIONAL_SEARCH = 'CONVERSATIONAL_SEARCH',
  PHONE_CALL = 'PHONE_CALL',
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
}

export enum HaldaWidget {
  SMART_FORM = 'SmartForm',
  HUB = 'Hub',
  AHA = 'Aha',
}

export enum HaldaVariant {
  SMART_FORM_VARIANT = 'SmartFormVariant',
  NONE = '',
}

export enum WidgetFeatureFlags {
  DLP_RESPONSE_PAGE_IN_HUB = 'DLP_RESPONSE_PAGE_IN_HUB',
  MODERN_STYLE = 'MODERN_STYLE',
}

export enum TargetType {
  ALL = 'ALL',
  DLP_TARGETED_PAGES = 'DLP_TARGETED_PAGES',
  CUSTOM = 'CUSTOM',
}

export const missingTargetUrlsText = 'Target URLs';

export enum TargetConditions {
  EXACT = 'EXACT',
  CONTAINS = 'CONTAINS',
}

export interface TargetUrl {
  id?: string;
  url: string;
  targetCondition: TargetConditions;
  isIncluded: boolean;
}

// This object mirrors the table attributes. No joins.
export interface SmartFormBasicInfo {
  id: string;
  name: string;
}

export interface SmartForm {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  detailedName: string;
  targetType: TargetType;
  includedUrls: Array<TargetUrl>;
  excludedUrls: Array<TargetUrl>;
  targetUtms: { [key: string]: string } | null;
  isPublished: boolean;
  isArchived: boolean;
  targetCountries: { targetingType: any, countries: Array<string> };
  targetStates: { targetingType: any, states: Array<string> };
  conversionRate: number;
  sampleTargetUrl: string;
  websiteUrl: string;
  isExportOptional: boolean;
  isIntegrationActiveMap: { [integrationUid: string]: boolean } | null;
  segmentRoutingType?: SegmentRoutingType | null;
  isClickToPopConfigEnabled: boolean,
  isEmbeddedConfigEnabled: boolean,
  isEmbeddedFormDynamicHeightEnabled: boolean,
  isHubStepOnly: boolean,
  recommendedUrls?: Array<RecommendedUrl>;
  routeToBuilder?: string;
  rfiOverrideUrl?: string;
  rfiOverrideCssStyles?: string;
}

export interface SmartFormForIntegrationsPage {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  detailedName: string;
  isPublished: boolean;
  isArchived: boolean;
}
export interface SmartFormWithStats {
  name?: string;
  id: string;
  isPublished: boolean;
  stats?: {
    visits: string,
    leads: string,
    conversionRate: string,
  };
  hasPreviousExport?: boolean;
  numLeadsSinceLastExport?: number;
  publishedVariantIds?: Array<string>
  sampleTargetUrl: string;
  integrationsList?: string[];
}

export interface GenerationInProgressForm {
  id: string;
  name: string;
  backgroundJobId: string;
}

export enum CoverageDisplayTypes {
  COVERED = 'COVERED',
  UNCOVERED = 'UNCOVERED',
}

export enum ContentAnalyticsDisplayTypes {
  ANSWERED_QUESTIONS = 'ANSWERED_QUESTIONS',
  UNANSWERED_QUESTIONS = 'UNANSWERED_QUESTIONS',
}

export interface AiSeoDashboardData {
  answeredFeedbackData: Array<{ name: string; value: number; color: string }>;
  answeredCommentsCount: number;
  unansweredFeedbackData: Array<{ name: string; value: number; color: string }>;
  unansweredCommentsCount: number;
  answeredCategoryData: Array<{ label: string; value: number }>;
  unansweredCategoryData: Array<{ label: string; value: number }>;
  totalQuestions: number;
  totalAnswered: number;
  answeredPercentage: number;
}

export interface PageInfo {
  id: string;
  url: string;
  monthlyTraffic: number;
  urlCategoryId: string;
  isExcluded: boolean;
  isChecked?: boolean;
  isBeingRemoved?: boolean;
}

export interface ImageFormatVariant {
  webp: string;
  jpeg: string;
}

export interface ImageVariants {
  thumbnail: ImageFormatVariant;
  full: ImageFormatVariant;
}

export interface LandingPage {
  id: string;
  uid?: string;
  name?: string;
  smartFormId?: string;
  billboardContent: string;
  headerContent?: string | null;
  formHeaderContent?: string | null;
  title?: string | null;
  backgroundImageUrl: string | null;
  backgroundImageVariants?: ImageVariants | null;
  backgroundVideoUrl?: string | null;
  isEnabled: boolean;
  isArchived?: boolean;
  landingPageUrl?: string;
  previewUrl?: string;
  bannerEnabled?: boolean;
  bannerColor?: string | null;
  bannerLogoUrl?: string | null;
  bannerLogoVariants?: ImageVariants | null;
  backgroundColor?: string | null;
  overlayEnabled?: boolean;
  overlayColor?: string | null;
  overlayOpacity?: number;
  footerEnabled?: boolean;
  footerColor?: string | null;
}

export interface CustomDomain {
  uid: string;
  domain: string;
  verificationStatus: 'pending' | 'verified' | 'failed';
  verifiedAt: string | null;
  sslStatus: 'pending' | 'provisioning' | 'active' | 'failed';
  sslProvisionedAt: string | null;
  isActive: boolean;
  createdAt: string;
  acmValidationCnameName: string | null;
  acmValidationCnameValue: string | null;
  cloudfrontDomainName: string | null;
}

export interface LandingPageDataWithCustomizations {
  id: string;
  headerContent?: string | null;
  formHeaderContent?: string | null;
  billboardContent: string;
  backgroundVideoUrl?: string | null;
  backgroundImageUrl: string | null;
  backgroundImageVariants?: ImageVariants | null;
  bannerEnabled: boolean;
  bannerColor: string | null;
  bannerLogoUrl: string | null;
  bannerLogoVariants?: ImageVariants;
  backgroundColor: string | null;
  overlayEnabled: boolean;
  overlayColor: string | null;
  overlayOpacity: number;
  footerEnabled: boolean;
  footerColor: string | null;
  isEnabled: boolean;
}

export interface LandingPageStats {
  totalViews: number;
  totalInquiries: number;
  conversionRate: number;
}

export interface LandingPageMetrics {
  totalViews: number;
  avgConversionRate: number;
  totalInquiries: number;
}

export interface SmartFormOption {
  id: string;
  name: string;
}

export interface RecommendedUrl {
  id: string;
  url: string;
  monthlyTraffic: number;
  isChecked?: boolean;
  isBeingRemoved?: boolean;
}

export interface UrlCategory {
  name: string;
  id: string;
  numPages: number;
  coveragePercentage?: number;
}

export interface SmartFormTemplate {
  id: string;
  name: string;
  description: string;
  previewUrl: string;
  templateThumbnailImage: string;
  isRecommended: boolean;
  isTrending: boolean;
  isNew: boolean;
  screens: Array<CustomizableStandardFormScreenDataType>;
  industry: Industry;
  managingPartner: ManagingPartner;
}

export interface SmartFormCoverage {
  id: string;
  name: string;
  coverage: number;
  converstion: number;
  numOfPages: number;
}

export enum TemplateFormat {
  STANDARD = 'SMART_FORM', // legacy db value
  CUSTOM_OFFER_URL = 'CUSTOM_OFFER_URL',
}

export enum DisplayMethod {
  MODAL = 'MODAL',
  MODAL_MEDALLION = 'MODAL_MEDALLION',
  SLIDE_OUT = 'SLIDE_OUT',
}

export enum BillboardDisplayMethod {
  SPLIT_SCREEN = 'SPLIT_SCREEN',
  FULL_SCREEN = 'FULL_SCREEN',
  NONE = 'NONE',
}

export const BillboardDisplayMethodReadable = {
  [BillboardDisplayMethod.SPLIT_SCREEN]: 'Split Screen',
  [BillboardDisplayMethod.FULL_SCREEN]: 'Full Screen',
  [BillboardDisplayMethod.NONE]: 'No Billboard',
};

export enum BillboardButtonIconType {
  NONE = 'NONE',
  CHEVRON = 'CHEVRON',
  BIG = 'BIG',
  SMALL = 'SMALL',
  LONG = 'LONG',
}

export const BillboardButtonIconTypeReadable = {
  [BillboardButtonIconType.NONE]: 'None',
};

export enum VariantTemplateClass {
  CUSTOM_SMART_FORM_URL = 'VariantTemplates::CustomSmartFormUrl',
  CUSTOMIZABLE_STANDARD_FORM = 'VariantTemplates::CustomizableStandardForm',
}

export interface SmartFormVariant {
  id: string;
  createdAt: Date;
  showOnMobile: boolean;
  showOnDesktop: boolean;
  isPublished: boolean;
  sampleWeight: number;
  discard: boolean;
  name?: string;
  customOfferUrl?: string;
  isArchived: boolean;
  triggerType?: TriggerType;
  triggerValue?: number;
  frequencyType?: FrequencyType;
  frequencyValue?: number;
  variantTemplateType?: VariantTemplateClass;
  variantTemplateId?: number;
  templateFormat: TemplateFormat;
  displayMethod: DisplayMethod;
  slideOutTabCtaText?: string;
  slideOutTabBackgroundColorHex?: string;
  slideOutTabTextColorHex?: string;
  smartFormName: string;
  poweredByHaldaUrl: string;
  hubStepImgSrc: string;
  hubStepHeading: string;
  hubStepButtonText: string;
  hubStepSubheading: string;
  hubStepImageAltText: string;
  isOffSiteCommsDisabled: boolean;
  modernStyleEnabled?: boolean;
}

export interface SmartFormVariantForHubPage {
  id: string;
  isPublished: boolean;
  discard: boolean;
  name: string;
  isArchived: boolean;
  isDiscarded: boolean;
}

export interface SmartFormWithVariants {
  id: string;
  variants: Array<SmartFormVariantForHubPage>
  isUsedInHub?: boolean;
  name: string;
  isPublished: boolean;
}

export type SmartFormIncludesSmartFormVariant = SmartForm & { smartFormVariant: SmartFormVariant };

export enum UserRole {
  MEMBER = 'MEMBER',
  PARTNER_ADMIN = 'PARTNER_ADMIN',
  HALDA_ADMIN = 'HALDA_ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
}

export const readableUserRole = (userRole: UserRole) => {
  switch (userRole) {
    case UserRole.MEMBER:
      return 'Member';
    case UserRole.PARTNER_ADMIN:
      return 'Partner Admin';
    case UserRole.HALDA_ADMIN:
      return 'Halda Admin';
    case UserRole.SUPER_ADMIN:
      return 'Super Admin';
    default:
      return 'Member';
  }
};

export const AnyAdmin = [
  UserRole.HALDA_ADMIN, UserRole.SUPER_ADMIN, UserRole.PARTNER_ADMIN,
];

export interface FontFace {
  filename: string,
  attachmentId: number,
  link: string,
  format: string,
  weight: string | number,
  style: string,
}

export interface AccountFont {
  id: string;
  createdAt: Date;
  name: string;
  fontFaces: Array<FontFace>;
}

import { FONT_IMPORTS as IMPORTED_FONT_IMPORTS, DEFAULT_FONT_FAMILIES_ARRAY } from './utils/font-imports';

export const DEFAULT_FONT_FAMILIES = DEFAULT_FONT_FAMILIES_ARRAY;

export const FONT_IMPORTS = IMPORTED_FONT_IMPORTS;

export const DEFAULT_FONT_SIZES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 24, 36, 48, 64];

export interface Industry {
  id: string;
  name: string;
  prospectTerm: string;
  verbPhrase: string;
  repType: string;
  businessType: string;
  goal: string;
}

export interface ManagingPartner {
  id: string;
  name: string;
}

export enum AccountStatus {
  FREE_TRIAL = 'FREE_TRIAL',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  INTERNAL = 'INTERNAL',
}

export enum AccountTimezone {
  HAWAII = 'Hawaii',
  PACIFIC = 'Pacific Time (US & Canada)',
  MOUNTAIN = 'Mountain Time (US & Canada)',
  CENTRAL = 'Central Time (US & Canada)',
  EASTERN = 'Eastern Time (US & Canada)',
  DUBLIN = 'Dublin',
}

export interface Timezone {
  key: AccountTimezone,
  name: string,
  tzId: string,
}

export const TIMEZONES = new Map<AccountTimezone, Timezone>([
  [AccountTimezone.HAWAII, {
    key: AccountTimezone.HAWAII,
    name: 'HT - Hawaii Time (UTC-10:00)',
    tzId: 'US/Hawaii',
  }],
  [AccountTimezone.PACIFIC, {
    key: AccountTimezone.PACIFIC,
    name: 'PT - Pacific Time (UTC-08:00)',
    tzId: 'US/Pacific',
  }],
  [AccountTimezone.MOUNTAIN, {
    key: AccountTimezone.MOUNTAIN,
    name: 'MT - Mountain Time (UTC-07:00)',
    tzId: 'US/Mountain',
  }],
  [AccountTimezone.CENTRAL, {
    key: AccountTimezone.CENTRAL,
    name: 'CT - Central Time (UTC-06:00)',
    tzId: 'US/Central',
  }],
  [AccountTimezone.EASTERN, {
    key: AccountTimezone.EASTERN,
    name: 'ET - Eastern Time (UTC-05:00)',
    tzId: 'US/Eastern',
  }],
  [AccountTimezone.DUBLIN, {
    key: AccountTimezone.DUBLIN,
    name: 'IST - Irish Standard Time (UTC+01:00)',
    tzId: 'Europe/Dublin',
  }],
]);

export const DEFAULT_TIMEZONE: Timezone = {
  key: AccountTimezone.MOUNTAIN,
  name: 'MT - Mountain Time (UTC-07:00)',
  tzId: 'US/Mountain',
};

export enum FreeTrialStatus {
  LIVE = 'LIVE',
  EXPIRED = 'EXPIRED',
  PENDING = 'PENDING',
}

export interface AhaSearchSuggestion {
  id: string;
  prompt: string;
  isArchived: string;
}

export interface AhaCommonQuestion {
  id: string;
  question: string;
  isArchived: boolean;
}

export interface AhaAuditResultQuestion {
  id: string;
  record_id?: number;
  query: string;
  answer: string;
  knew_answer: boolean;
  source_urls: string[];
  account_id: string;
  aha_audit_result_id: string;
  ai_audit_questionable_type: string;
  ai_audit_questionable_id: string;
  backup_engine_type: string;
  is_suggested_search: boolean;
  response_time?: number;
  search_engine?: string;
  created_at: string;
  updated_at: string;
  topic_card_id?: string | null;
  was_topic_card_used?: boolean;
}

export interface RagieSyncSummary {
  total_count: number;
  ready_count: number;
  pending_count: number;
  failed_count: number;
  unsynced_count: number;
  failed_card_ids: number[];
  is_semantic_search_enabled: boolean;
}

export interface AuditMetadata {
  use_ragie_override?: boolean;
  ragie_sync_summary?: RagieSyncSummary;
  semantic_search_enabled?: boolean;
  audit_timestamp?: string;
  audit_type?: string;
  segment_uids?: string[];
  segment_names?: string[];
}

export interface AhaAuditResult {
  id: string;
  account_id: string;
  global_audit_id?: string;
  created_at: string;
  updated_at: string;
  metadata?: AuditMetadata;
  account?: {
    id: string;
    name: string;
  };
  aha_audit_result_questions?: AhaAuditResultQuestion[];
  aha_audit_result_questions_count?: number;
  correct_answers_count?: number;
  score_percentage?: number;
  totalQuestions?: number;
  reviewedQuestions?: number;
  viewedCount?: number;
  ratedCount?: number;
  isInProgress?: boolean;
  reviewScore?: number;
  knowsAnswerScore?: number;
  auditListName?: string;
  jobUid?: string;
  segmentNames?: string[];
  archivedAt?: string | null;
  createdByUserId?: string | null;
}

export interface BrandToneDataPoint {
  id: string;
  name: string;
  value?: string;
}

export interface AhaEngineCanaryQuestion {
  id: string;
  accountId: string;
  accountName: string;
  questionText: string;
  goldenAnswer: string;
  isActive: boolean;
  ahaCommonQuestionId?: string;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface EngineCanaryResult {
  id: string;
  created_at: string;
  account: {
    id: string;
    name: string;
  };
  correct_answers_count: number;
  total_questions: number;
  score_percentage: number;
}

export interface EngineCanaryResultsByEngine {
  perplexity: EngineCanaryResult[];
  gemini: EngineCanaryResult[];
  halda: EngineCanaryResult[];
}


export enum ZIndexValues {
  BUILDER_SIDEBAR_ITEM = 3,
  DRAGGING_BUILDER_SIDEBAR_ITEM = 4,
  PRIORITIZE_BILLBOARD_IN_BUILDER = 9,
  INPUT_FIELD_LABEL = 10,
  FUNNEL_ANALYTICS_OVERLAYS = 10,
  CLOSE_X_BUTTON = 20,
  UNIVERSAL_HEADER = 1200,
  SIDEBAR_FLYOUT = 1202,
  MODAL = 1400,
  COMBOBOX_OPTIONS_IN_MODAL = 1450,
  // Alerts must render above modals, including the combobox options a modal can host.
  ALERT = 1500,
}

export interface GoogleAnalyticsIntegration {
  implementationMethod?: ThirdPartyAnalyticsImplementationType,
  gtagDestinationId?: string,
  gtmContainerId?: string,
  isActive: boolean,
  syncFormSubmit: boolean,
  syncFormView: boolean,
  syncHubView: boolean,
  syncHubCtaClick: boolean,
  syncHubFormSubmit: boolean,
  syncHubSearchSubmit: boolean,
  syncHubQuickLinkClick: boolean,
  syncSearchOpened: boolean,
  syncSearchSuggestionClick: boolean,
  syncSearchSubmit: boolean,
  syncSearchFormSubmit: boolean,
  syncSearchResultLinkClick: boolean,
  syncSearchClosed: boolean,
  syncLandingPageFormSubmit: boolean,
  syncLandingPageFormView: boolean,
}

export interface GoogleAnalyticsConfiguration {
  implementationMethod?: ThirdPartyAnalyticsImplementationType,
  gtagDestinationId?: string,
  gtmContainerId?: string,
  isActive: boolean,
  isAutoDetected: boolean,
  selectedEvents: string[],
  syncLandingPageFormSubmit: boolean,
  syncLandingPageFormView: boolean,
}

export interface HotjarIntegration {
  hotjarInstanceId?: string,
  isActive: boolean,
}

// This object mirrors the table attributes. No joins.
export interface AccountBasicInfo {
  id: string;
  accountName: string;
  subdomain?: string;
  isAccountLandingPageOnly?: boolean;
  isSegmentBasedRoutingEnabled?: boolean;
}

export enum IpedsStudentTypes {
  UNDERGRAD = 'undergrad',
  GRAD = 'grad',
  BOTH = 'both',
}

export interface Account {
  id: string;
  accountName: string;
  calendarLink: string;
  termsAndConditionsUrl: string,
  privacyPolicyUrl: string,
  homePageURL: string;
  industry: Industry;
  managingPartner: ManagingPartner;
  logoSrc: string;
  smartFormHeroLogoSrc: string;
  ahaLogoSrc: string;
  ahaSearchSuggestions: Array<string>;
  headingColorHex: string;
  headingFont: string;
  subheadingColorHex: string;
  subheadingFont: string;
  paragraphFont: string;
  backgroundButtonColorHex: string;
  buttonTextColorHex: string;
  backgroundBillboardColorHex: string;
  hidePoweredByHalda: boolean;
  accountFonts: AccountFont[];
  accountStatus: AccountStatus;
  freeTrialStartDate: string;
  freeTrialEndDate: string;
  isArchived: boolean;
  isAhaDarkMode: boolean;
  isInEducationIndustry: boolean;
  widgetsZIndex: number;
  timezone: AccountTimezone;
  hubspotId: string;
  googleTagManagerContainerId: string;
  brandTone: BrandToneDataPoint[];
  emailStyleGuide?: EmailStyleGuide;
  isMfaRequired: boolean;
  syncLeadRecommendationWithIntegrations: boolean;
  leadRecommendationPrompt?: string;
  isGtmManagerActive: boolean;
  googleAnalyticsConfiguration?: GoogleAnalyticsIntegration;
  primaryColor: string;
  hotjarIntegration?: HotjarIntegration;
  isNewHubEnabled: boolean;
  isNewHubDemoEnabled: boolean;
  isAgenticHubEnabled: boolean;
  cantFindAnswerMessage?: string;
  isDlpResponsePageInHubEnabled: boolean;
  nicheUrl?: string;
  ipedsUnitId?: string;
  ipedsStudentType?: IpedsStudentTypes;
  lastYearEnrollment?: number;
  isPhoneCallEnabled: boolean;
  isSmsEnabled: boolean;
  isBehavioralSmsEnabled: boolean;
  isSmsDataCaptureEnabled: boolean;
  isSmsCampaignsEnabled?: boolean;
  isWhatsappEnabled: boolean;
  isCallTransferEnabled: boolean;
  isSmsCallHandoffEnabled: boolean;
  allowRealtimeVoiceModels: boolean;
  whatsAppNumber: string;
  phoneNumberPretty: string;
  isClickToPopAhaInHubEnabled: boolean;
  isViewAsStudentEnabled: boolean;
  analyticsPageUrl: string;
  isSegmentationAiSearchEnabled: boolean;
  isSegmentBasedRoutingEnabled: boolean;
  isAutoAdvanceEnabled: boolean;
  isBrandPostureEnabled: boolean;
  isComebackEmailConfigV3Enabled: boolean;
  isComebackSimulationEnabled: boolean;
  isComebackEmailOutputsEnabled: boolean;
  isDripCampaignEmailOutputsEnabled: boolean;
  isAhaFeedbackSurveyEnabled: boolean;
  isEmailPromptCustomizationEnabled: boolean;
  isFieldDuplicateDetectionEnabled: boolean;
  hasLandingPageFeature: boolean;
  isAccountLandingPageOnly: boolean;
  subdomain: string;
  purchasedRecords: number;
  whitelistedDomains?: string[];
}

export interface SmartFormVariantFormattedForDevMenu {
  id: string;
  name: string;
}

export interface SmartFormFormattedForDevMenu {
  id: string;
  name: string;
  variants: SmartFormVariantFormattedForDevMenu[];
}

export interface AccountFormattedForDevMenu {
  id: string;
  name: string;
  accountStatus: AccountStatus;
  smartForms: SmartFormFormattedForDevMenu[];
  isArchived: boolean;
}

export enum UtmTypeKey {
  UTM_MEDIUM = 'UTM_MEDIUM',
  UTM_SOURCE = 'UTM_SOURCE',
  UTM_CAMPAIGN = 'UTM_CAMPAIGN',
  UTM_TERM = 'UTM_TERM',
  UTM_CONTENT = 'UTM_CONTENT',
}

export enum DepartmentType {
  MARKETING = 'MARKETING',
  ADMISSIONS = 'ADMISSIONS',
  OTHER = 'OTHER',
}

export enum BusinessRoleType {
  DECISION_MAKER = 'DECISION_MAKER',
  ADMINISTRATOR = 'ADMINISTRATOR',
  USER = 'USER',
}

export enum ProgramType {
  UNDERGRAD = 'undergrad', // lower because of legacy db values
  GRAD = 'grad',
  MBA = 'mba',
}

export enum CtaTypes {
  LINK = 'link',
  MAILTO_LINK = 'mailto_link',
}

export enum CurrencyName {
  USD = 'USD',
}

export enum CurrencySymbol {
  USD = '$',
}

export enum FlepFieldStatus {
  REQUIRED = 'required',
  HIDDEN = 'hidden',
  OPTIONAL = 'optional',
}

export enum DateRange {
  ALL_TIME = 'ALL_TIME',
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  LAST_SEVEN_DAYS = 'LAST_SEVEN_DAYS',
  LAST_THIRTY_DAYS = 'LAST_THIRTY_DAYS',
  LAST_WEEK = 'LAST_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  LAST_QUARTER = 'LAST_QUARTER',
  LAST_YEAR = 'LAST_YEAR',
  WEEK_TO_DATE = 'WEEK_TO_DATE',
  MONTH_TO_DATE = 'MONTH_TO_DATE',
  QUARTER_TO_DATE = 'QUARTER_TO_DATE',
  YEAR_TO_DATE = 'YEAR_TO_DATE',
  CUSTOM = 'CUSTOM',
}

export const dateRangeNameLookup = {
  [DateRange.ALL_TIME]: 'All Time',
  [DateRange.TODAY]: 'Today',
  [DateRange.YESTERDAY]: 'Yesterday',
  [DateRange.LAST_SEVEN_DAYS]: 'Last 7 Days',
  [DateRange.LAST_THIRTY_DAYS]: 'Last 30 Days',
  [DateRange.LAST_WEEK]: 'Last Week',
  [DateRange.LAST_MONTH]: 'Last Month',
  [DateRange.LAST_QUARTER]: 'Last Quarter',
  [DateRange.LAST_YEAR]: 'Last Year',
  [DateRange.WEEK_TO_DATE]: 'Week to date',
  [DateRange.MONTH_TO_DATE]: 'Month to date',
  [DateRange.QUARTER_TO_DATE]: 'Quarter to date',
  [DateRange.YEAR_TO_DATE]: 'Year to date',
  [DateRange.CUSTOM]: 'Custom',
};


export enum MomentTimezone {
  UTC = 'UTC',
  MOUNTAIN = 'America/Denver',
  PACIFIC = 'America/Los_Angeles',
  CENTRAL = 'America/Detroit',
  EASTERN = 'America/New_York',
  HAWAII = 'Pacific/Honolulu',
  DUBLIN = 'Europe/Dublin',
}

export interface LeadsIpData {
  ip: string;
  lat: number;
  long: number;
  city: string;
  state: string;
  country: string;
  countryCode: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
}

export interface FormFieldData {
  name: string;
  responses: Array<{ formResponse: string; leadCount: number; }>;
  dataType: 'numeric' | 'categorical' | 'hidden';
  min?: number | null;
  max?: number | null;
  step?: number;
}

export enum FunnelStageDesignations {
  IMPRESSIONS = 'impressions',
  INQUIRIES = 'inquiries',
  APPLICATION_SUBMITTED = 'application_submitted',
  ENROLLED = 'enrolled',
}

export interface FunnelStage {
  id: string;
  title: string;
  nextStageProjection: number;
  orderNumber: number;
  hasImportedData: boolean;
  designation: FunnelStageDesignations | null;
}

export interface FunnelData {
  smartFormData: Array<{ smartFormName: string; smartFormId: string; stageData: Array<number> }>
  overallConversionRate: number;
  isCustomFunnel: boolean;
  stages: Array<FunnelStage>;
}

export type VariantFunnelCount = { count: number; };
export type SmartFormFunnelCount = { count: number; variants: Record<string, VariantFunnelCount>; };
export type FunnelStageFunnelCount = { count: number; smartForms: Record<string, SmartFormFunnelCount>; };
export type FunnelStageCounts = Record<string, FunnelStageFunnelCount>;

export interface FunnelDataV2 {
  funnelStageCounts: FunnelStageCounts;
}

export interface FunnelPermissions {
  hasImportedData: boolean;
  viewAdministrator: boolean;
  manageIntegrations: boolean;
}

export interface DynamicCta {
  id: string;
  funnelStage: FunnelStage;
  ctaText: string;
  ctaDescriptionText: string;
  url: string;
}

export interface AnalyticsDataTop {
  totalVisits: number;
  totalLeads: number;
  totalPartialLeads: number;
  totalConversionRate: number;
  totalSecondaryActions: number;
  conversionRateSecondaryActions: number;
  hasLeadsBeforeApril62022: boolean;
  // Add leads/views back, mark new fields as optional for V1
  conversionRateByDay: Array<{ date: string, conversionRate: number, leads: number, views: number }>;
  totalLeadCount?: number | null; // Optional
  leadCountByDay?: Array<{ date: string; count: number }> | null; // Optional
  prevYearTotalConversionRate?: number | null; // Optional
  prevYearConversionRateByDay?: Array<{ date: string, conversionRate: number, leads: number, views: number }> | null; // Optional
  prevYearTotalLeadCount?: number | null; // Optional
  prevYearLeadCountByDay?: Array<{ date: string; count: number }> | null; // Optional
}

export interface AnalyticsDataV2Top {
  totalVisits: number;
  totalLeads: number;
  totalPartialLeads: number;
  totalConversionRate: number;
  totalSecondaryActions: number;
  conversionRateSecondaryActions: number;
  hasLeadsBeforeApril62022: boolean;
  hasOldHubMetrics: boolean;
  oldHubMedallionImpressions: number;
  oldHubFormSubmissions: number;
  conversionRateByDay: Array<{ date: string, conversionRate: number, leads: number, views: number }>;
  totalLeadCount: number;
  leadCountByDay: Array<{ date: string; count: number }>;

  hasHubMetrics: boolean;
  hubMedallionImpressions: number;
  hubVisits: number;
  hubStepViews: number;
  hubFormSubmissions: number;
  hubTotalConversionRate: number;
  hubConversionRateByDay: Array<{ date: string, conversionRate: number, leads: number, views: number }>;
  hubTotalLeadCount: number;
  hubLeadCountByDay: Array<{ date: string; count: number }>;

  prevYearTotalConversionRate?: number | null;
  prevYearConversionRateByDay?: Array<{ date: string, conversionRate: number, leads: number, views: number }> | null;
  prevYearTotalLeadCount?: number | null;
  prevYearLeadCountByDay?: Array<{ date: string; count: number }> | null;
  prevYearHubTotalConversionRate?: number | null;
  prevYearHubConversionRateByDay?: Array<{ date: string, conversionRate: number, leads: number, views: number }> | null;
  prevYearHubTotalLeadCount?: number | null;
  prevYearHubLeadCountByDay?: Array<{ date: string; count: number }> | null;
}
export interface AnalyticsDataBottom {
  conversionDataByUrl: Array<{
    url: string;
    visitCount: number;
    leadCount: number;
    conversionRate: number;
    isActive: boolean;
  }>;
  leadCountByDeviceType: Array<{ deviceType: string, leadCount: number }>;
  leadCountByBrowser: Array<{ browser: string, leadCount: number }>;
  leadCountsByFormResponse: {
    [formField: string]: FormFieldData;
  };
}

export interface AnalyticsDataMap {
  leadsWithIpAddress?: Array<LeadsIpData>;
  leadCountByState: Array<{ state: string, leadCount: number }>;
  leadCountByCountry: Array<{ country: string, leadCount: number }>;
}

export enum SortDirection {
  ASCENDING = 'ascending',
  DESCENDING = 'descending',
}

export enum TargetingLocationsRules {
  INCLUDE = 'INCLUDE',
  EXCLUDE = 'EXCLUDE',
}

export enum TargetLevel {
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
}

export enum SecondaryActionType {
  cta_button_click = 'cta_button_click',
  link_click = 'link_click',
}

export enum MetricType {
  TOTAL_VISITS = 'Total Visits',
  TOTAL_LEADS = 'Total Inquiries',
  PARTIAL_LEADS = 'Partial Inquiries',
  TOTAL_SUBMISSIONS = 'Total Submissions',
  PARTIAL_SUBMISSIONS = 'Partial Submissions',
  SECONDARY_ACTIONS = 'Secondary Actions',
  SECONDARY_RATE = 'Secondary Rate',
  CONVERSION_RATE = 'Conversion Rate',
  MEDALLION_IMPRESSIONS = 'Launcher Impressions',
  MEDALLION_CLICKS = 'Launcher Clicks',
  MEDALLION_CTR = 'Launcher CTR',
  CTA_BUTTON_CLICKS = 'CTA Button Clicks',
  CTA_BUTTON_CTR = 'CTA Button CTR',
  INCREMENTAL_CONV_PERCENT = 'Conversion Increase %',
  AHA_TOTAL_SEARCHES = 'Total Searches',
  AHA_AVERAGE_VISITS_PER_USER = 'Visits Per User (Avg.)',
  AHA_DLP_IMPRESSIONS = 'AI Form Impressions',
  AHA_DLP_SUBMISSIONS = 'AI Form Submissions',
  HUB_VISITS = 'Hub Visits',
  HUB_STEP_VIEWS = 'Hub Step Views',
  HUB_SUBMISSIONS = 'Hub Submissions',
}

export interface AIResponseSubscription {
  funcToCall: () => void;
  funcToCheckIfFinished: () => boolean;
  responseType: string;
  hasBeenTriggered: boolean;
};

export interface Metric {
  label: string;
  value?: string | number;
  statisticDescription?: string;
  showSpecialMessage?: boolean;
  specialString?: string;
}

export interface ChartDataPoint {
  id?: string;
  label: string;
  value: number;
}

export enum QuestionType {
  DROPDOWN = 'DROPDOWN',
  RADIO_BUTTON = 'RADIO_BUTTON',
  SHORT_ANSWER = 'SHORT_ANSWER',
  SLIDER = 'SLIDER',
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  HIGH_SCHOOL = 'HIGH_SCHOOL',
  ZIP_CODE = 'ZIP_CODE',
  EMAIL_ADDRESS = 'EMAIL_ADDRESS',
  PHONE_NUMBER = 'PHONE_NUMBER',
  PROGRAM_START = 'PROGRAM_START',
  DATE_OF_BIRTH = 'DATE_OF_BIRTH',
  QUESTION_GROUP = 'QUESTION_GROUP',
  LIST = 'LIST',
  CHECKBOX = 'CHECKBOX',
  DATE_PICKER = 'DATE_PICKER',
  PARAGRAPH = 'PARAGRAPH',
  STATEMENT = 'TEXT_AREA',
}

export const QuestionLabels: { [key in QuestionType]: string } = {
  [QuestionType.FIRST_NAME]: 'First Name',
  [QuestionType.LAST_NAME]: 'Last Name',
  [QuestionType.EMAIL_ADDRESS]: 'Email Address',
  [QuestionType.PHONE_NUMBER]: 'Phone Number',
  [QuestionType.PROGRAM_START]: 'Program Start',
  [QuestionType.DATE_OF_BIRTH]: 'Date of Birth',
  [QuestionType.ZIP_CODE]: 'Zip Code',
  [QuestionType.HIGH_SCHOOL]: 'High School',
  [QuestionType.DROPDOWN]: 'Dropdown',
  [QuestionType.RADIO_BUTTON]: 'Radio Button',
  [QuestionType.LIST]: 'List',
  [QuestionType.CHECKBOX]: 'Checkbox',
  [QuestionType.SHORT_ANSWER]: 'Short Answer',
  [QuestionType.SLIDER]: 'Slider',
  [QuestionType.QUESTION_GROUP]: 'Question Group',
  [QuestionType.DATE_PICKER]: 'Date Picker',
  [QuestionType.PARAGRAPH]: 'Paragraph',
  [QuestionType.STATEMENT]: 'Statement'
}

export const HardCodedQuestionTypes: QuestionType[] = [
  QuestionType.FIRST_NAME,
  QuestionType.LAST_NAME,
  QuestionType.EMAIL_ADDRESS,
  QuestionType.PHONE_NUMBER,
  QuestionType.DATE_OF_BIRTH,
  QuestionType.PROGRAM_START,
  QuestionType.HIGH_SCHOOL,
  QuestionType.ZIP_CODE,
];

export const QuestionTypesThatDontRequireFieldConfig = [
  ...HardCodedQuestionTypes,
  QuestionType.QUESTION_GROUP,
  QuestionType.STATEMENT,
]

export const QuestionLabelsThatDontRequireFieldConfig: string[] = QuestionTypesThatDontRequireFieldConfig.map(
  type => QuestionLabels[type]
).concat('Text Area');

export const hardCodedFormInputNames = ['first_name', 'last_name', 'email_address', 'phone_number', 'date_of_birth', 'program_start_season', 'program_start_term', 'program_start_year', 'high_school', 'zip_code', 'paragraph', 'lead_recommendation'];

export const templateFormInputNames = ['current_annual_salary', 'gmat_score', 'gpa', 'date_of_birth',
  'years_of_work_experience', 'student_type', 'age', 'biggest_concern', 'biggest_motivation', 'is_us_citizen_or_resident', 'program_of_interest', 'instate_residency',
  'funnel_stage', 'area_of_interest_question', 'has_applied_for_federal_aid', 'student_type', 'receives_employer_reimbursement'];

export const defaultFormInputNames = hardCodedFormInputNames.concat(templateFormInputNames);

export enum NavItemType {
  MAIN = 'MAIN',
  QUESTION = 'QUESTION',
  HIDDEN_STATIC_ANSWER = 'HIDDEN_STATIC_ANSWER',
}

export type NavItem = {
  id?: number;
  name: string;
  href: string;
  iconComponent?: ElementType;
  type: NavItemType,
  questionLogicId?: number;
}

export type ResponsePageResponseType = FormResponseType | ResponseType;

export enum FormResponseType {
  PROMO_CODE = 'PROMO_CODE',
  EQUATION = 'EQUATION',
  TEXT_AREA = 'TEXT_AREA',
  THANK_YOU_NAME = 'THANK_YOU_NAME',
  CONVERSATIONAL_SEARCH_INPUT = 'CONVERSATIONAL_SEARCH_INPUT',
}

export enum AnswerResponseType {
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE',
}

export enum ResponseType {
  STATIC = 'STATIC',
  DYNAMIC = 'DYNAMIC',
  NONE = 'NONE',
  THRESHOLD = 'THRESHOLD',
  BENCHMARK = 'BENCHMARK',
  AUTO_GENERATED = 'AUTO_GENERATED',
}

export enum SegmentRoutingType {
  STATIC = 'STATIC',
  DYNAMIC = 'DYNAMIC',
  NONE = 'NONE',
}

// Response Type Readable Mapping
export const ResponseTypeReadable = {
  [ResponseType.STATIC]: 'Static',
  [ResponseType.DYNAMIC]: 'Dynamic',
  [ResponseType.NONE]: 'None',
  [ResponseType.THRESHOLD]: 'Threshold',
  [ResponseType.BENCHMARK]: 'Benchmark',
  [ResponseType.AUTO_GENERATED]: 'Auto Generated',
};

// make this into its own set of classes given its polymorphic nature
export interface QuestionData {
  type: QuestionType;
  formInputName: string;
  fieldName?: string;
  isRequired: boolean;
  placeholder: string;
  question: string;
  id?: number;
  createdAt: Date | string;
  archivedAt?: Date | string;
  disclaimerText?: string;
  questionNamePlaceholder: string;
  uid: string;
  fieldUid?: string;
  name?: string;
  isCreatingNew?: boolean;
  responseType?: ResponseType;
  responsePosition?: number;
  screenIdx?: number;
  questionIdx?: number;
}

export enum QualifiedType {
  QUALIFIED = 'QUALIFIED',
  UNQUALIFIED = 'UNQUALIFIED',
}

export enum PromoCodeType {
  SINGLE_CODE = 'SINGLE_CODE',
  MULTIPLE_CODES = 'MULTIPLE_CODES',
}

export enum EquationResultFormatType {
  NONE = 'NONE',
  WHOLE_NUMBER = 'WHOLE_NUMBER',
  DECIMAL = 'DECIMAL',
  DOLLAR = 'DOLLAR',
}

export enum BenchmarkResultType {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum SliderNumberFormat {
  CURRENCY = 'currency',
  FLOAT = 'float',
  INTEGER = 'integer',
}

export interface Option {
  uid: string;
  name: string;
  value: string;
}

export interface DropdownOption {
  uid: string;
  name: string;
  value: string;
  response?: string;
  sourceUrl?: string;
  optionId: number;
  path?: string;
  qualifiedStatus?: QualifiedType | null;
  fieldOptionUid: string;
  oneToOneDynamicResponseUid?: string;
  responseScreenElementUid?: string;
  thresholdOptionQualificationUid?: string;
  metadata?: Record<string, string>;
}

import type {
  ConditionalOptionGroup,
} from './components/application/smart-forms/builder/screens/conditional-logic/types';

export type { ConditionalOptionGroup };

export interface QuestionAndOptions {
  question: string;
  questionId: string;
  options: Array<{ optionId: number; value: string; }>;
  isSkippable: boolean;
}

export interface ApplicationDeadlinesObject {
  fall?: {
    day: number,
    month: string,
  },
  spring?: {
    day: number,
    month: string,
  },
  summer?: {
    day: number,
    month: string,
  },
  winter?: {
    day: number,
    month: string,
  }
}

export interface ProgramStartSeasonDeadline {
  month: string;
  day?: number | null;
}

export interface ProgramStartSeason {
  key: string;
  name: string;
  show: boolean;
  deadline?: ProgramStartSeasonDeadline | null;
}

export interface PreviousListlikeQuestionAndOptions {
  question: string;
  questionId: number;
  options: Array<DropdownOption>;
  formInputName: string;
  conditionalOptionGroups?: Array<ConditionalOptionGroup>;
}

export interface QuestionGroupData extends QuestionData {
  type: QuestionType.QUESTION_GROUP;
}
export interface DropdownQuestionData extends QuestionData {
  type: QuestionType.DROPDOWN;
  options: Array<DropdownOption>;
  conditionalOptionGroups: Array<ConditionalOptionGroup>;
  formInputBorderRadius?: number;
  response?: string;
  responseType: ResponseType;
  qualifiedResponse: string;
  unqualifiedResponse: string;
  optionConfigMode?: 'manual' | 'attribute';
  attributeFilterKey?: string;
  attributeFilterValue?: string;
  attributeFilterExpression?: AttributeFilterExpression | null;
  groupByKey?: string;
  showFieldOptionLabel?: boolean;
}

export interface VisibleButtonQuestionData extends QuestionData {
  options: Array<DropdownOption>;
  conditionalOptionGroups: Array<ConditionalOptionGroup>;
  response?: string;
  responseType: ResponseType;
  qualifiedResponse: string;
  unqualifiedResponse: string;
  hasOtherOption: boolean;
  otherPlaceholderText?: string;
  otherResponseType?: ResponseType;
  optionConfigMode?: 'manual' | 'attribute';
  attributeFilterKey?: string;
  attributeFilterValue?: string;
  attributeFilterExpression?: AttributeFilterExpression | null;
  groupByKey?: string;
  showFieldOptionLabel?: boolean;
}

export interface RadioButtonQuestionData extends VisibleButtonQuestionData {
  type: QuestionType.RADIO_BUTTON;
}

export interface ListQuestionData extends VisibleButtonQuestionData {
  type: QuestionType.LIST;
}

export interface CheckboxQuestionData extends QuestionData {
  type: QuestionType.CHECKBOX;
  options: Array<DropdownOption>;
  conditionalOptionGroups: Array<ConditionalOptionGroup>;
  response?: string;
  optionConfigMode?: 'manual' | 'attribute';
  attributeFilterKey?: string;
  attributeFilterValue?: string;
  attributeFilterExpression?: AttributeFilterExpression | null;
  groupByKey?: string;
  showFieldOptionLabel?: boolean;
}

export const asListLikeQuestion = (question: QuestionData) => question as (
  DropdownQuestionData |
  RadioButtonQuestionData |
  ListQuestionData |
  CheckboxQuestionData
);

export interface SliderQuestionData extends QuestionData {
  type: QuestionType.SLIDER;
  min: number;
  max: number;
  step: number;
  screenIdx: number;
  questionIdx: number;
  defaultValue: number;
  numberFormat: string;
  formInputBorderRadius?: number;
  responseType: ResponseType;
  response?: string;
  qualifiedResponse: string;
  unqualifiedResponse: string;
  thresholdValue?: number;
  thresholdLabel?: string;
  higherThresholdValue?: string;
  lowerThresholdValue?: string;
  highThresholdResponse?: string,
  mediumThresholdResponse?: string,
  lowThresholdResponse?: string,
  isInvertedThreshold?: boolean;
}

export enum ProgramStartDisplayType {
  DROPDOWN = 'DROPDOWN',
  RADIO_BUTTON = 'RADIO_BUTTON',
  LIST = 'LIST',
}

export interface ProgramStartQuestionData extends QuestionData {
  type: QuestionType.PROGRAM_START;
  applicationDeadlines: ApplicationDeadlinesObject;
  numSeasonsDisplay: number;
  // Legacy include booleans; superseded by `seasons` but still dual-written
  // until the backend columns are dropped.
  programStartIncludeWinter: boolean;
  programStartIncludeSpring: boolean;
  programStartIncludeSummer: boolean;
  programStartIncludeFall: boolean;
  seasons: ProgramStartSeason[];
  formInputBorderRadius?: number;
  displayType: ProgramStartDisplayType;
}

export interface ShortAnswerQuestionData extends QuestionData {
  type: QuestionType.SHORT_ANSWER;
  formInputBorderRadius?: number;
  autoGeneratedDisclaimerText?: string;
  showSourceLink?: boolean;
  response?: string;
  screenIdx: number;
}

export interface ParagraphQuestionData extends QuestionData {
  type: QuestionType.PARAGRAPH;
  formInputBorderRadius?: number;
  autoGeneratedDisclaimerText?: string;
  showSourceLink?: boolean;
  response?: string;
  screenIdx: number;
}

export interface FirstNameQuestionData extends QuestionData {
  type: QuestionType.FIRST_NAME;
  formInputBorderRadius?: number;
}
export interface StatementQuestionData extends QuestionData {
  type: QuestionType.STATEMENT;
  text: string;
}

export interface LastNameQuestionData extends QuestionData {
  type: QuestionType.LAST_NAME;
  formInputBorderRadius?: number;
}

export interface EmailAddressQuestionData extends QuestionData {
  type: QuestionType.EMAIL_ADDRESS;
  formInputBorderRadius?: number;
}

export interface PhoneNumberQuestionData extends QuestionData {
  type: QuestionType.PHONE_NUMBER;
  formInputBorderRadius?: number;
  shouldValidate?: boolean;
}

export interface ZipCodeQuestionData extends QuestionData {
  type: QuestionType.ZIP_CODE;
  formInputBorderRadius?: number;
}

export interface HighSchoolQuestionData extends QuestionData {
  type: QuestionType.HIGH_SCHOOL;
  formInputBorderRadius?: number;
}

export interface DateOfBirthQuestionData extends QuestionData {
  type: QuestionType.DATE_OF_BIRTH;
  formInputBorderRadius?: number;
}
export interface DatePickerQuestionData extends QuestionData {
  type: QuestionType.DATE_PICKER;
  formInputBorderRadius?: number;
}

export const ArchivableQuestions = [
  QuestionType.CHECKBOX,
  QuestionType.SLIDER,
  QuestionType.DROPDOWN,
  QuestionType.SHORT_ANSWER,
  QuestionType.DATE_PICKER,
  QuestionType.PROGRAM_START,
  QuestionType.RADIO_BUTTON,
  QuestionType.LIST,
];

export type AnyQuestionData = DropdownQuestionData | RadioButtonQuestionData
  | ListQuestionData | ShortAnswerQuestionData
  | FirstNameQuestionData | LastNameQuestionData
  | EmailAddressQuestionData | PhoneNumberQuestionData
  | DateOfBirthQuestionData | ProgramStartQuestionData
  | ZipCodeQuestionData | HighSchoolQuestionData
  | SliderQuestionData | CheckboxQuestionData
  | DateOfBirthQuestionData | DatePickerQuestionData
  | ParagraphQuestionData | StatementQuestionData | QuestionGroupData;

export type StaticResponseEnabledQuestionData = SliderQuestionData | ParagraphQuestionData
  | ShortAnswerQuestionData | RadioButtonQuestionData;

export const QuestionTypesWithResponses = [
  QuestionType.SHORT_ANSWER,
  QuestionType.PARAGRAPH,
  QuestionType.SLIDER,
  QuestionType.DROPDOWN,
  QuestionType.CHECKBOX,
  QuestionType.RADIO_BUTTON,
  QuestionType.LIST,
] as const;

export type QuestionDataWithResponse = DropdownQuestionData | SliderQuestionData
  | ShortAnswerQuestionData | ParagraphQuestionData | CheckboxQuestionData
  | RadioButtonQuestionData | ListQuestionData;

export enum CustomizableStandardScreenType {
  STANDARD = 'STANDARD',
  STANDARD_FLEP = 'STANDARD_FLEP',
  STANDARD_RESULTS_ONE_TO_ONE_RESPONSE = 'STANDARD_RESULTS_ONE_TO_ONE_RESPONSE',
  STANDARD_QUESTIONS = 'STANDARD_QUESTIONS',
}

export interface FormResponse {
  key: string;
  type: FormResponseType;
  fieldName: string;
  fieldUid: string;
  fieldValue: string;
  equationResponseUid?: string;
  equationsBox?: string;
  equationResultFormatType?: EquationResultFormatType;
  singlePromoCode?: string;
  promoCodeType?: PromoCodeType;
  text?: string;
  showConfetti?: boolean;
  promoCodeResponseUid?: string;
}

export interface AhaElement {
  uid: string;
  key: string;
  isEnabled: boolean;
  promptText?: string;
  placeholderText?: string;
}

export interface CustomizableStandardFormScreenData {
  uid?: string;
  type: CustomizableStandardScreenType;
  questions?: Array<AnyQuestionData>;
}

export enum FieldType {
  DATE_PICKER = 'QuestionDetailsDatePicker',
  LISTLIKE = 'QuestionDetailsListlike',
  MULTISELECT = 'QuestionDetailsMultiselect',
  NUMBER = 'QuestionDetailsNumber',
  PHONE_NUMBER = 'QuestionDetailsPhoneNumber',
  PROGRAM_START = 'QuestionDetailsProgramStart',
  TEXT = 'QuestionDetailsText',
  LISTLIKE_WITH_OTHER = 'QuestionDetailsListlikeWithOther',
}

export const OptionFieldTypes: FieldType[] = [
  FieldType.LISTLIKE,
  FieldType.MULTISELECT,
  FieldType.LISTLIKE_WITH_OTHER,
];

export const FieldTypeReadable: Record<FieldType, string> = {
  [FieldType.TEXT]: 'Text',
  [FieldType.NUMBER]: 'Numeric',
  [FieldType.LISTLIKE]: 'List',
  [FieldType.MULTISELECT]: 'Multi-Select',
  [FieldType.LISTLIKE_WITH_OTHER]: 'List + Other',
  [FieldType.DATE_PICKER]: 'Date',
  [FieldType.PHONE_NUMBER]: 'Phone',
  [FieldType.PROGRAM_START]: 'Program Start',
};

export const RESPONSE_TYPES_BY_FIELD_TYPE: Record<FieldType, ResponseType[]> = {
  [FieldType.TEXT]:                [ResponseType.NONE, ResponseType.STATIC, ResponseType.AUTO_GENERATED],
  [FieldType.NUMBER]:              [ResponseType.NONE, ResponseType.STATIC, ResponseType.THRESHOLD, ResponseType.BENCHMARK],
  [FieldType.LISTLIKE]:            [ResponseType.NONE, ResponseType.STATIC, ResponseType.DYNAMIC, ResponseType.THRESHOLD],
  [FieldType.MULTISELECT]:         [ResponseType.NONE, ResponseType.STATIC, ResponseType.DYNAMIC],
  [FieldType.LISTLIKE_WITH_OTHER]: [ResponseType.NONE, ResponseType.STATIC, ResponseType.DYNAMIC, ResponseType.THRESHOLD],
  [FieldType.DATE_PICKER]:         [ResponseType.NONE],
  [FieldType.PHONE_NUMBER]:        [ResponseType.NONE],
  [FieldType.PROGRAM_START]:       [ResponseType.NONE],
};

export interface FieldConfig {
  phoneValidate?: boolean;
  phoneDisclaimerText?: string;
  numericMin?: number;
  numericMax?: number;
  numericDefaultValue?: number;
  numericStep?: number;
  numericFormat?: string;
  thresholdValue?: number;
  isInvertedThreshold?: boolean;
  benchmarkLowerThreshold?: string;
  benchmarkHigherThreshold?: string;
  benchmarkLowResponse?: string;
  benchmarkMediumResponse?: string;
  benchmarkHighResponse?: string;
  autoGeneratedDisclaimerText?: string;
  autoGeneratedShowSourceLink?: boolean;
  otherResponseType?: ResponseType;
  otherResponse?: string;
  otherAutoGeneratedDisclaimerText?: string;
  otherAutoGeneratedShowSourceLink?: boolean;
}

export enum NonQuestionFieldType {
  SUBMISSION_INFO = 'SubmissionInfo',
  PROFILE_ENRICHMENT_DATA = 'ProfileEnrichmentData',
  ATTRIBUTION_DATA = 'AttributeData',
  IP_DATA = 'IpData',
  PHONE_INTERACTION_TYPE = 'PhoneInteractionType',
  FORM_RESPONSE = 'FormResponse',
  AI_SEARCH_RESPONSE = 'AiSearchResponse',
}

export interface ConditionalSkipField {
  uid: string;
  formInputName: string | undefined,
  question: string | undefined,
  fieldUid: string | undefined,
  skipIfNoResponse: boolean;
  fieldType: FieldType;
  hiddenStaticAnswerUid: string | undefined;
}

export interface ConditionalSkipOption {
  uid: string;
  fieldOptionUid: string;
  value: string;
  conditionalSkipFieldUid: string;
}

export const NO_OPTION_SELECTED = "<No Option Selected>";

export interface CustomizableStandardFormQuestionScreenData
  extends CustomizableStandardFormScreenData {
  type: CustomizableStandardScreenType.STANDARD | CustomizableStandardScreenType.STANDARD_QUESTIONS;
  questions: Array<AnyQuestionData>;
  previousButtonUid?: string;
  previousButtonText?: string;
  nextButtonUid: string;
  nextButtonText: string;
  iconComponent: React.FC<any>;
  conditionalSkipField?: ConditionalSkipField | null;
  conditionalSkipOptions?: ConditionalSkipOption[];
  conditionalLogicConfig?: ConditionalLogicConfig | null;
}

export interface CustomizableStandardFormFlepScreenData
  extends CustomizableStandardFormScreenData {
  type: CustomizableStandardScreenType;
  questions: Array<AnyQuestionData>;
  previousButtonText?: string;
  nextButtonText: string;
  iconComponent: React.FC<any>;
  submissionDisclaimerParagraph?: string;
}

export enum ResponseParagraphType {
  AI_GENERATED = 'AI_GENERATED',
  STATIC = 'STATIC',
}

export interface CustomizableStandardFormResultsOneToOneResponseScreenData
  extends CustomizableStandardFormScreenData {
  ctaFontColorHex: string;
  type: CustomizableStandardScreenType.STANDARD_RESULTS_ONE_TO_ONE_RESPONSE;
  ctaParagraphType: ResponseParagraphType;
  ctaParagraphInstructions?: string;
  ctaParagraphDefaultValue?: string;
  ctaParagraph?: string;
  ctaParagraphUid?: string;
  ctaParagraphScreenElementUid?: string;
  topParagraphType?: ResponseParagraphType;
  topParagraphInstructions?: string;
  topParagraphDefaultValue?: string;
  topParagraphUid?: string;
  topParagraphScreenElementUid?: string;
  topParagraph?: string;
  formResponses: Array<FormResponse>;
  ahaElement?: AhaElement;
  ctaText: string;
  ctaButtonBackgroundColorHex: string;
  ctaType: CtaTypes;
  ctaLink: string;
}

export type CustomizableStandardFormScreenDataType = CustomizableStandardFormScreenData
  | CustomizableStandardFormQuestionScreenData
  | CustomizableStandardFormFlepScreenData
  | CustomizableStandardFormResultsOneToOneResponseScreenData

export interface QuestionInterface {
  formInputName: string;
  renderQuestion: () => ReactElement;
}

export interface ResponsePageItemInterface {
  isFinishedRendering: () => boolean;
  setFinishedRenderingTrue: () => void;
  renderResponse: ({ watch }: { watch: () => any; }) => ReactElement;
  getType: () => string;
  getFormInputName: () => string;
  getResponsePosition: () => number | undefined;
}

export interface ResponsePageItemProps {
  Component: FunctionComponent<any>;
  [key: string]: any;
}

export interface ResponsePageItemComponentProps {
  [key: string]: any;
}

export type ResponseData = any;

export interface FormResponseInterface {
  renderFormResponse: () => ReactElement;
}

export enum BillboardBackgroundType {
  SOLID_COLOR = 'SOLID_COLOR',
  GRADIENT = 'GRADIENT',
}

export enum ImageOperation {
  UPLOAD = 'upload',
  REMOVE = 'remove',
}

export interface VariantImage {
  id: string;
  src: string;
  alt: string;
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  shouldShowOnMobile?: boolean;
}

export enum AnswerSource {
  STATIC = 'static',
  URL = 'url',
}

export interface HiddenStaticAnswer {
  uid: string;
  fieldUid?: string;
  fieldName: string;
  answer: string;
  value: string;
  answerSource?: AnswerSource;
  urlParameterName?: string | null;
  fallbackValue?: string | null;
  requireParameter?: boolean;
  fieldType?: FieldType;
  fieldOptions?: FieldOption[];
  isCreatingNew?: boolean;
  conditionalSkipField?: ConditionalSkipField | null;
  conditionalSkipOptions?: ConditionalSkipOption[];
  conditionalLogicConfig?: ConditionalLogicConfig | null;
}

export enum ScreenButtonAlignmentType {
  left = 'left',
  right = 'right',
  center = 'center',
}

export enum NavButtonTypes {
  COLORED = 'COLORED',
  TEXT_AND_CHEVRON = 'TEXT_AND_CHEVRON',
  CHEVRON_ONLY = 'CHEVRON_ONLY',
}

export enum NavButtonBorderStyles {
  SQUARE = 'SQUARE',
  ROUND = 'ROUND',
  FULL_RADIUS = 'FULL_RADIUS',
}

export interface CustomizableStandardForm {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  archivedQuestions: Array<AnyQuestionData>;
  billboardHeadingText: string;
  billboardSubheadingText: string;
  billboardLogoImageAltText: string;
  billboardBackgroundColorHex: string;
  billboardBackgroundStartingGradientHex: string;
  billboardBackgroundEndingGradientHex: string;
  billboardBackgroundType: BillboardBackgroundType;
  billboardDisplayMethod: BillboardDisplayMethod;
  mobileBillboardDisplayMethod: BillboardDisplayMethod;
  billboardButtonText: string;
  defaultButtonColorHex: string;
  billboardButtonColorHex: string;
  billboardButtonTextColorHex: string;
  billboardButtonIcon: BillboardButtonIconType;
  hiddenStaticAnswers: Array<HiddenStaticAnswer>;
  resultsPageCtaLink?: string;
  resultsPageCtaType?: string;
  screenParagraphFont: string;
  screenNextButtonBackgroundColorHex: string;
  screenNavButtonsFont: string;
  screenNavButtonsType: NavButtonTypes;
  screenNavButtonsBorderStyle: NavButtonBorderStyles;
  screenNextButtonText: string;
  screenNextButtonFontColorHex: string;
  screenButtonAlignment: ScreenButtonAlignmentType;
  screens: Array<CustomizableStandardFormScreenDataType>;
  billboardLogoImageSrc: string;
  billboardLogoEnabled: boolean;
  abTestHasProgressBar: boolean;
  mobileImages: Array<VariantImage>;
  desktopImages: Array<VariantImage>;
  smartFormVariantId: string;
  smartFormTemplateId?: string;
  fieldUidsWithMappings: Array<string>;
  requiredIndicatorText: string;
}

export interface SmartFormVariantForStatisticsPage {
  id: string;
  smartFormName: string;
  accountName: string;
  previewLink: string;
}

export interface SmartFormVariantForSmartFormShowPage extends SmartFormVariant {
  variantTemplate: CustomizableStandardForm;
  totalVisitCount: number;
  totalLeadCount: number;
  abTestVisitCount: number;
  abTestLeadCount: number;
  previewUrl: string;
  editUrl: string;
  hasBeenInABTest: boolean;
  isDiscarded: boolean;
  updatedAt: Date;
}

export enum AbTestComponentName {
  MICRO_RESPONSES = 'MICRO_RESPONSES',
  DUMMY_AB_TEST = 'DUMMY_AB_TEST',
  BILLBOARD_TEASER = 'BILLBOARD_TEASER',
  FANCY_PROGRESS_BAR = 'FANCY_PROGRESS_BAR',
  SPLIT_FLEP = 'SPLIT_FLEP',
  DANCING_GUY = 'DANCING_GUY',
  DROPDOWN_TO_LIST_RADIO_BUTTONS = 'DROPDOWN_TO_LIST_RADIO_BUTTONS',
}

export interface AbTestComponent {
  id: string;
  name: AbTestComponentName;
  properties: GeneralHash;
}

export interface GlobalAbTestComponent extends AbTestComponent {
  enabled: boolean;
}



export interface VariantTemplateSettingsType extends CustomizableStandardForm, SmartFormVariant { }

export enum ImageUploadComponentTypes {
  DARK_MODE = 'DARK_MODE',
  WITH_PREVIEW = 'WITH_PREVIEW',
  NO_PREVIEW = 'NO_PREVIEW',
}

export interface ImageUploadComponentProps {
  isDraggedOver: boolean;
  handleDragOver: DragEventHandler;
  handleFileDrop: DragEventHandler;
  handleDragLeave: DragEventHandler;
  handleInputClick: MouseEventHandler;
  handleUpload: FormEventHandler;
  handleRemoveImageClick: () => void;
  hasRemovedImg: boolean;
  uploadedFile: File;
  imgSrc: string;
  formInputName: string;
  uploadedImgFileName: string;
  errorMsg: string;
  isLoading: boolean;
  preferredWidth: number;
  preferredHeight: number;
  containerWidth?: number;
  containerHeight?: number;
  imageMaxWidth?: number;
  imageMaxHeight?: number;
  sizeLimitText: string;
  notes?: string;
  isDisabled?: boolean;
  accountId?: string | undefined;
  uploadFile: (file: File) => void;
}

export interface SmartFormBuilderFormData {
  smartForm: SmartForm;
  smartFormVariant: SmartFormVariant;
  variantTemplate: CustomizableStandardForm;
}

export interface SmartFormBuilderState {
  activeQuestionId: number | undefined;
  showErrorAlert: boolean;
  showSaveConflictAlert: boolean;
  alertMsg: string,
  mostRecentSaveTime: string,
  mostRecentSaveUserEmail: string,
  viewVariantDataUrl: string,
}

export enum PreviewDisplay {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

export interface SmartFormBuilderRenderStepParams {
  globalDispatch: React.Dispatch<GlobalReducerAction>;
  globalState: SmartFormBuilderState;
}

export interface SmartFormBuilderRenderFunc {
  (params: SmartFormBuilderRenderStepParams): ReactElement;
}

export interface SmartFormBuilderRoute {
  name: string;
  iconComponent?: React.FC<any>;
  href: string;
  renderStepFunc: SmartFormBuilderRenderFunc;
  type: NavItemType;
}

export interface IntegrationConnectionForBuilder {
  name: string,
  id: string,
  type: IntegrationType,
  isActive: boolean,
  hasActiveIntegrationWithThisSmartForm: boolean,
  integrationId: string,
}

export interface MoveQuestionProps {
  sourceScreenIndex: number,
  destScreenIndex: number,
  sourceQuestionIndex?: number,
  destQuestionIndex?: number,
}

export enum CtaButtonTextLength {
  MAX_LENGTH = 35,
}

export interface DotsVerticalMenuItem {
  text: string;
  link: string;
  params?: { [key: string]: string | number };
  method: string;
  target?: string;
  removeAuthToken?: boolean;
}

export interface DailyMetric {
  date: string;
  count: number;
}

export interface EmailMetricsData {
  emailsDelivered: number;
  uniqueOpens: number;
  uniqueOpenRate: number;
  uniqueClicks: number;
  uniqueCtr: number;
  optOutRate: number;
  totalPageViews: number;
  totalHubInteractions: number;
  isEmailPerformanceConnected: boolean;
  hasSlateEmailConfig?: boolean;
  hasSalesforceEmailConfig?: boolean;
  emailsDeliveredByDay?: DailyMetric[];
  uniqueOpensByDay?: DailyMetric[];
  uniqueClicksByDay?: DailyMetric[];
  pageViewsByDay?: DailyMetric[];
  hubInteractionsByDay?: DailyMetric[];
}

export interface LeadProfile {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  date?: string;
  id?: string;
  shareableUrl?: string;
  isTest?: boolean;
}

export interface ExtendedLeadProfile extends LeadProfile {
  formData?: { [key: string]: any };
  stage?: string;
  location?: string;
  leadRecommendation?: string;
  engagementScore?: number;
  engagementLevel?: string;
}

export interface LeadProfileDetails {
  questionName: string;
  questionFullText: string;
  formInputName: string;
  response: string;
}

export interface ColumnData {
  sample: string[];
  emptyCount: number;
  uniqueValuesCount: number;
}

export enum LeadIngestionStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface LeadIngestionCsvPreviewData {
  headers: string[];
  previewRows: Record<string, string>[];
  columnsData: Record<string, ColumnData>;
  fileName: string;
  totalRows?: number;
  leadIngestionHistoryUid?: string;
}

export enum SlateLeadImportConfigurationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export interface SlateLeadImportConfiguration {
  uid: string;
  name: string;
  endDate: string | null;
  totalRecordLimit: number | null;
  status: SlateLeadImportConfigurationStatus;
  createdAt: string;
  updatedAt: string;
  webService: {
    uid: string;
    endpointUrl: string;
    username: string;
    httpMethod: string;
    lastSyncedAt: string | null;
  } | null;
  totalRecordsCreated: number;
  lastSyncAt: string | null;
  connectedCampaignName: string | null;
  connectionTested?: boolean;
  hasUnprocessedResponses?: boolean;
}

export interface LeadIngestionHistorySummary {
  uid: string;
  name: string;
  fileName: string;
  status: string;
  totalRows: number;
  imported: number;
  createdAt: string;
  source: string;
}

export interface LeadIngestionImportResults {
  success: boolean;
  totalRows: number;
  imported: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export interface LeadIngestionHistory {
  uid: string;
  name: string;
}

export enum LeadProfileEvent {
  AI_SMS_MESSAGE = 'AI SMS Message',
  AI_EMAIL_RECEIPT = 'AI Email Receipt',
  AI_PHONE_CONVERSATION = 'AI Phone Call Conversation',
  AHA_INITIAL_CHAT = 'AHA Initial Chat',
  HUB_SWITCH = 'Hub Switch',
  PAGE_VIEW = 'Page View',
  DLP_COMPLETE_SUBMISSION = 'DLP Complete Submission',
  DLP_PARTIAL_SUBMISSION = 'DLP Partial Submission',
  DLP_VISIT = 'DLP Visit',
  DLP_SECONDARY_ACTION_CLICK = 'DLP Secondary Action Click',
  HUB_VISIT = 'Hub Visit',
  HUB_COMPLETE_SUBMISSION = 'Hub Complete Submission',
  HUB_PARTIAL_SUBMISSION = 'Hub Partial Submission',
  HUB_FINAL_CTA_CLICK = 'Hub Final Cta Click',
  HUB_OPT_OUT = 'Hub Opt Out',
  HUB_QUICK_LINK_CLICK = 'Hub Quick Link Click',
  HUB_STEP_SECONDARY_ACTION_CLICK = 'Hub Step Secondary Action Click',
  AHA_MESSAGE = 'AI Search Message',
  WIDGET_FEEDBACK = 'Widget Feedback',
  AHA_RESPONSE_FEEDBACK = 'Aha Response Feedback',
}

export interface FireTopLevelEventMessage {
  event?: string;
  type?: string;
  error?: unknown;
}

export interface HandleSecondaryActionClickMessage extends FireTopLevelEventMessage {
  type: string;
  actionType: SecondaryActionType;
  actionUrl: string;
  ctaText: string;
  variantId: string;
}

export interface VariantSwappedMessage extends FireTopLevelEventMessage {
  type: HaldaMessageType.VARIANT_SWAPPED;
  variantId: string;
  newVariantId: string;
}

export interface ResolvedFormLink {
  url: string;
  isFormLink: boolean;
  modalUrl: string | null;
  variantId: string | null;
}

export const readableLeadProfileEvent = (event: LeadProfileEvent) => {
  switch (event) {
    case LeadProfileEvent.AHA_INITIAL_CHAT:
      return 'AI Search Initial Chat';
    case LeadProfileEvent.DLP_COMPLETE_SUBMISSION:
      return 'AI Form Complete Submission';
    case LeadProfileEvent.DLP_PARTIAL_SUBMISSION:
      return 'AI Form Partial Submission';
    case LeadProfileEvent.DLP_VISIT:
      return 'AI Form Visit';
    case LeadProfileEvent.DLP_SECONDARY_ACTION_CLICK:
      return 'AI Form Secondary Action Click';
    case LeadProfileEvent.AHA_MESSAGE:
      return 'AI Search Message';
    case LeadProfileEvent.WIDGET_FEEDBACK:
      return 'Student Feedback - Form Submission';
    case LeadProfileEvent.AHA_RESPONSE_FEEDBACK:
      return 'Student Feedback - AI Search';
    default:
      return event;
  }
};

export interface SmsConversationMessage {
  uid: string;
  message: string;
  direction: string;
  timestamp: string;
}

export interface SmsExpandableDetails {
  type: 'sms';
  message: string;
  direction: string;
  conversation?: SmsConversationMessage[];
}

export interface EmailExpandableDetails {
  type: 'email';
  subject: string;
  body: string;
  preheader?: string;
}

export interface PhoneCallExpandableDetails {
  type: 'phone_call';
  summary?: string;
  transcript?: string;
  durationSeconds?: number;
  recordingUrl?: string;
  callType?: string;
  phoneNumber?: string;
}

export interface FeedbackExpandableDetails {
  type: 'feedback';
  feedbackType: string;
  feedbackText: string;
  feedbackSource: string;
  queryId?: string;
  smartFormUid?: string;
}

export type ExpandableEventDetails = SmsExpandableDetails | EmailExpandableDetails | PhoneCallExpandableDetails | FeedbackExpandableDetails;

export interface LeadProfileEventData {
  uid: string;
  url: string;
  date: string;
  eventType: LeadProfileEvent;
  widgetName?: string;
  widgetUid?: string;
  expandableDetails?: ExpandableEventDetails;
}

export interface ScreenContainerProps {
  headingText: string;
  subheadingText: string;
  children?: React.ReactNode;
  billboardLogoEnabled?: boolean;
  mobileImages: VariantImage[];
  desktopImages: VariantImage[];
  mobileBillboardRef?: (node: HTMLDivElement) => void;
  screenType?: CustomizableStandardScreenType;
  screenHasPhoneQuestion?: boolean;
  isMobile?: boolean;
}

export type ContainerComponent = React.FC<ScreenContainerProps>;

// SMS opt-in consent captured on smart-form question screens and threaded
// alongside (never inside) formData through the smart-form submission POST.
// Only the user's checkbox action is sent to the server. The disclosure text and
// checkbox label are authored server-side from the versioned DisclosureVersion,
// so the client no longer submits (forgeable) evidence copy.
export interface SmsConsentSubmissionPayload {
  isChecked: boolean;
}

export enum DisclaimerModes {
  SMS = 'sms',
  PHONE = 'phone',
  FORM_OPT_IN = 'form_opt_in',
}

export enum DisclosureVersionKeys {
  RESPONSE_PAGE_SMS = 'response_page_sms',
  RESPONSE_PAGE_PHONE = 'response_page_phone',
}

// The versioned disclosure copy served with the page config; body carries a
// literal {accountName} token interpolated client-side at render time.
export interface DisclosureVersion {
  uid: string;
  key: string;
  body: string;
}

export interface FindDisclosureVersionProps {
  disclosureVersions: DisclosureVersion[] | undefined;
  key: DisclosureVersionKeys;
}

export const findDisclosureVersion = (
  { disclosureVersions, key }: FindDisclosureVersionProps,
): DisclosureVersion | undefined => disclosureVersions?.find((version) => version.key === key);

export interface CommonScreenProps {
  allFields: any;
  entranceAnimationClasses?: string;
  animateDuration?: number;
  registerFn: UseFormRegister<any>;
  handleQuestionSubmit?: (formInputNames?: Array<string>) => void;
  isPreview: boolean;
}

export interface FormDetails {
  questions: Record<string, string>;
  billboardHeading: string;
  billboardSubheading: string;
  smartFormName: string;
}

export interface PartialOneToOneResponseResultsScreenProps {
  allQuestions: Array<QuestionDataWithResponse>;
  topParagraph: string;
  topParagraphType?: ResponseParagraphType;
  topParagraphInstructions?: string;
  topParagraphDefaultValue?: string;
  formResponses?: any;
  ctaParagraph: string;
  ctaLink: string;
  ctaText: string;
  ctaButtonColorHex: ColorHexCode;
  ctaFontColorHex: ColorHexCode;
  enableCalculation?: boolean;
  trackingId?: string;
  smartFormVariantId?: string;
  ahaElement?: AhaElement;
  isAhaElementDisabled: boolean;
  shouldHideAhaElement: boolean;
  isInBuilder?: boolean;
  setValue: UseFormSetValue<ResponseData>;
  watch: () => ResponseData;
  hubStepId?: string;
}

export interface PartialCustomizableStandardFlepScreenProps {
  questions: Array<AnyQuestionData>;
  submissionDisclaimerParagraph: string;
  nextButtonText?: string;
  backButtonText?: string;
  buttonAlignment?: ScreenButtonAlignmentType;
  disableButtons?: boolean;
  nextButtonBackgroundColorHex: ColorHexCode;
  nextButtonFontColorHex: ColorHexCode;
  screenIdx?: number;
  screenCount?: number;
  abTestHasProgressBar?: boolean;
  topParagraphType?: ResponseParagraphType;
  topParagraphInstructions?: string;
  topParagraphDefaultValue?: string;
}

export interface PartialCustomizableStandardScreenProps {
  questions: Array<AnyQuestionData>;
  allQuestionsData?: Array<AnyQuestionData>;
  buttonBackgroundColorHex: ColorHexCode;
  buttonFontColorHex: ColorHexCode;
  buttonAlignment?: ScreenButtonAlignmentType;
  backButtonText?: string;
  nextButtonText?: string;
  abTestHasProgressBar?: boolean;
  screenCount?: number;
  screenIdx?: number;
  screenNavButtonsFont?: string;
  screenNavButtonsType?: NavButtonTypes;
  screenNavButtonsBorderStyle?: NavButtonBorderStyles;
  disableButtons?: boolean;
  defaultNextButtonText?: string;
  shouldAlwaysShowMobileStyles?: boolean;
  conditionalSkipField?: ConditionalSkipField | null;
  conditionalSkipOptions?: ConditionalSkipOption[];
  conditionalLogicConfig?: ConditionalLogicConfig | null;
  topParagraphType?: ResponseParagraphType;
  topParagraphInstructions?: string;
  topParagraphDefaultValue?: string;
  isMobile?: boolean;
}

export type ScreenProps = PartialOneToOneResponseResultsScreenProps
  | PartialCustomizableStandardFlepScreenProps
  | PartialCustomizableStandardScreenProps;

export type ScreenComponent = React.FC<CommonScreenProps & PartialOneToOneResponseResultsScreenProps>
  | React.FC<CommonScreenProps & PartialCustomizableStandardFlepScreenProps>
  | React.FC<CommonScreenProps & PartialCustomizableStandardScreenProps>

export interface StandardVariantTemplateScreen {
  container: ContainerComponent;
  containerProps: ScreenContainerProps;
  screenComponent: ScreenComponent;
  screenProps: ScreenProps;
}

export interface FormSubmission {
  id: string;
  formData: GeneralHash;
  responseData: ResponseData;
}

export interface HubStep {
  id?: string;
  medallionCtaText: string;
  title: string;
  description: string;
  accountId: string;
  hubId: string;
  smartFormId: string;
  smartFormVariantId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  orderNumber: number;
  startHubStepExperienceCtaText: string;
  imageSrc: string;
  imageAltText: string;
  hubSteppableId: string;
  hubSteppableType: HubSteppableType;
  isComplete?: boolean;
  hasFeedback?: boolean;
  recommendationReason: string;
  submission?: FormSubmission;
}

export interface ContentCard extends HubStep {
  buttonLink?: string;
  logoSrcUrl?: string;
  showPoweredByLogo: boolean;
}

export interface HubScreen {
  id: string;
  questions: Array<QuestionInterface>;
}

export interface HubResourceLink {
  id: string;
  title: string;
  link: string;
  iconComponentName: string;
  iconComponentColorHex: string;
  accountId: string;
  orderNumber: number;
}

export interface Hub {
  id: string;
  name: string;
  finalCtaLink: string;
  finalCtaText: string;
  ctaDescriptionText: string;
  targetType: TargetType;
  includedUrls: Array<TargetUrl>;
  excludedUrls: Array<TargetUrl>;
  isPublished: boolean;
  hubSteps: Array<HubStep>;
  hubResourceLinks: Array<HubResourceLink>;
  headingText: string;
  isPreviewBtnDisabled: boolean;
  sampleTargetUrl: string;
  websiteUrl: string;
  poweredByHaldaUrl: string;
  isBillboardOnFirstScreenOfHubForm: boolean;
}

export interface NewHubColors {
  ctaBackgroundColorHex: string;
  ctaTextColorHex: string;
  suggestedSearchTextColorHex: string;
  questionBackgroundColorHex: string;
  medallionBackgroundColorHex: string;
  medallionIconColorHex: string;
  centerMedallionBackgroundColorHex: string;
  centerMedallionIconColorHex: string;
  centerMedallionTitleTextFontColorHex: string;
  centerMedallionSubtitleTextFontColorHex: string;
  centerMedallionNotificationTextFontColorHex: string;
  centerMedallionNotificationBackgroundColorHex: string;
  actionBarBackgroundColorHex: string;
  actionBarPrimaryButtonBackgroundColorHex: string;
  actionBarPrimaryButtonTextColorHex: string;
  actionBarActionIconColorHex: string;
}

export interface NewHub extends Hub, NewHubColors {
  medallionHeroIcon: string;
  centerMedallionHeroIcon: string;
  actionBarHeroIcon: string;
  actionBarPrimaryButtonLabel: string;
  actionBarTextActionLabel: string;
  actionBarCallActionLabel: string;
  actionBarShowTextAction: boolean;
  actionBarShowCallAction: boolean;
  hubLogoSrc: string;
  aha: NewHubAha;
  medallionType: MedallionType;
  isAiSearchInHubEnabled: boolean;
  isChatbotEnabled: boolean;
  // Serialized only when isChatbotEnabled is true (Hub#chatbot_config).
  chatbotAgentName?: string;
  chatbotAgentSubtitle?: string;
  chatbotSuggestedQuestions?: string[];
  chatbotDisclaimerText?: string;
  chatbotDisclaimerEnabled?: boolean;
}

export interface NewHubAha {
  suggestedQueries: string[];
  cta: string;
  disclaimerText: string;
  isDisclaimerTextEnabled: boolean;
}

export enum WebMessageSender {
  ASSISTANT = 'ASSISTANT',
  USER = 'USER',
}

export enum WebMessageSubtype {
  INBOUND = 'INBOUND',
  AI_RESPONSE = 'AI_RESPONSE',
  ERROR = 'ERROR',
}

export interface WebMessage {
  uid: string;
  message: string;
  sender: WebMessageSender;
  createdAt: string;
  messageType?: WebMessageSubtype;
}

export const DEFAULT_HUB_MEDALLION_ARIA_LABEL = 'Toggle Personalized AI Hub View';
export const DEFAULT_MEDALLION_TEXT = 'Take The Next Step';
export const DEFAULT_MEDALLION_SUBHEADING_TEXT = 'Click here for personalized information!';

export interface HubMetrics {
  medallionImpressions: number;
  medallionClicks: number;
  medallionCtr: number;
  totalLeads: number;
  avgNumOfAdditionalDataPoints: number;
  incrementalConvPercent: number;
  ctaButtonClicks: number;
  ctaButtonCtr: number;
  quickLinksClicks: number;
}

export interface CustomerSegment {
  id: string;
  name: string;
  description: string;
  accountId: string;
  isDefault: boolean;
  searchSourceUrl?: string;
  aiSearchContext?: string;
  createdAt: Date;
  updatedAt: Date;
  excludedUrlPaths?: string;
  canRequestAiCommunication?: boolean;
  isContentBasedRecommendationsEnabled: boolean;
  leadCount?: number;
  totalLeads?: number;
  percentage?: number;
  crmConnections?: SegmentCrmConnectionRef[];
  fieldCriteria?: SegmentFieldCriterion[];
  isDirty?: boolean;
  deterministicRulesChangedAt?: string;
}

export interface SegmentCrmConnectionRef {
  id: string;
  type: CrmConnectionType;
}

export interface SegmentData {
  uid: string;
  name: string;
  description: string;
  isDefault: boolean;
  ahaContext: string;
  ahaSourceDomains: string[];
  ahaExcludedUrlPaths: string[];
  crmConnections: SegmentCrmConnectionRef[];
  isTargetSegment: boolean;
  disableHaldaExperience: boolean;
  showSegmentIdentificationPopup: boolean;
  excludeFromAnalytics: boolean;
  suppressAiSummary: boolean;
  segmentPopupTitle: string | null;
  segmentPopupDescription: string | null;
  segmentPopupPosition: number | null;
  shouldShowInSegmentPopup: boolean;
}

export interface SegmentFieldCriterion {
  id: string;
  customerSegmentId: string;
  fieldId: string;
  fieldOptionId: string;
  fieldName: string;
  fieldOptionName: string;
}

export interface CrmConnectionForSegments {
  id: string;
  name: string;
  isActive: boolean;
}

export enum CrmConnectionType {
  SFTP = 'SftpConnection',
  SALESFORCE = 'SalesforceConnection',
  WEBHOOK = 'WebhookConnection',
}

export interface EmailTriggerExclusionItem {
  id: string;
  accountId: number;
  triggerId: string;
  customerSegmentId: string;
  funnelStageId: string;
}

export interface SegmentExclusionGroup {
  segmentId: string;
  stageIds: string[];
}

export type SaveExclusionsParams = {
  accountId: string;
  triggerId: string;
  segmentId: string;
  stageIds: string[];
};

export interface EmailTriggerExclusionsApi {
  createOrReplaceForSegment: (params: SaveExclusionsParams) => Promise<SegmentExclusionGroup>;
  updateForSegment: (params: SaveExclusionsParams) => Promise<SegmentExclusionGroup>;
  fetchExclusions: (triggerId: string) => Promise<EmailTriggerExclusionItem[]>;
  deleteForSegment: (params: { triggerId: string; segmentId: string; }) => Promise<void>;
}

export interface LeadProfileSegment {
  id: string;
  customerSegmentId: string;
  leadProfileId: string;
  matchScore?: number;
  explanation?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface HubBlockProps {
  name: string;
  id: string;
  isPreviewBtnDisabled: boolean;
  sampleTargetUrl: string;
}

export const DropdownLikeTypes = [QuestionType.DROPDOWN,
QuestionType.RADIO_BUTTON, QuestionType.LIST, QuestionType.CHECKBOX];

export const SelectionInputTypes = [QuestionType.SLIDER, QuestionType.DROPDOWN,
QuestionType.RADIO_BUTTON, QuestionType.LIST];


export const SKIP_LOGIC_ELIGIBLE_QUESTION_TYPES = [
  ...DropdownLikeTypes,
  QuestionType.SHORT_ANSWER,
  QuestionType.PHONE_NUMBER,
];

export interface DualListBoxConditionalSkipOption {
  label: string;
  value: string;
}

export const IconNames = [
  { componentName: 'AcademicCapIcon', name: 'academic-cap' },
  { componentName: 'BuildingLibraryIcon', name: 'building-library' },
  { componentName: 'BookOpenIcon', name: 'book-open' },
  { componentName: 'BookmarkIcon', name: 'bookmark' },
  { componentName: 'PencilIcon', name: 'pencil' },
  { componentName: 'CalendarIcon', name: 'calendar' },
  { componentName: 'CalculatorIcon', name: 'calculator' },
  { componentName: 'UserIcon', name: 'user' },
  { componentName: 'UsersIcon', name: 'users' },
  { componentName: 'BeakerIcon', name: 'beaker' },
  { componentName: 'BriefcaseIcon', name: 'briefcase' },
  { componentName: 'ChatBubbleOvalLeftEllipsisIcon', name: 'chat-bubble-oval-left-ellipsis' },
  { componentName: 'ClockIcon', name: 'clock' },
  { componentName: 'FlagIcon', name: 'flag' },
  { componentName: 'EnvelopeIcon', name: 'envelope' },
  { componentName: 'PhoneIcon', name: 'phone' },
  { componentName: 'MapPinIcon', name: 'map-pin' },
  { componentName: 'CurrencyDollarIcon', name: 'currency-dollar' },
  { componentName: 'StarIcon', name: 'star' },
  { componentName: 'ClipboardDocumentIcon', name: 'clipboard-document' },
  { componentName: 'ClipboardDocumentCheckIcon', name: 'clipboard-document-check' },
  { componentName: 'HomeIcon', name: 'home' },
  { componentName: 'MegaphoneIcon', name: 'megaphone' },
  { componentName: 'QuestionMarkCircleIcon', name: 'question-mark-circle' },
  { componentName: 'RectangleStackIcon', name: 'rectangle-stack' },
  { componentName: 'SparklesIcon', name: 'sparkles' },
  { componentName: 'Square2StackIcon', name: 'square-2-stack' },
  { componentName: 'Square3Stack3DIcon', name: 'square-3-stack-3d' },
  { componentName: 'Squares2X2Icon', name: 'squares-2x2' },
  { componentName: 'SquaresPlusIcon', name: 'squares-plus' },
];

export interface LauncherIconOption {
  componentName: string;
  name: string;
  label: string;
}

// Kept in sync with MedallionActionBar::MAX_*_LENGTH validations
export const ACTION_BAR_PRIMARY_LABEL_MAX_LENGTH = 30;
export const ACTION_BAR_ACTION_LABEL_MAX_LENGTH = 12;

export const LauncherIconOptions: LauncherIconOption[] = [
  { componentName: 'SparklesIcon', name: 'sparkles', label: 'Sparkle' },
  { componentName: 'ChatBubbleOvalLeftEllipsisIcon', name: 'chat-bubble-oval-left-ellipsis', label: 'Chat' },
  { componentName: 'QuestionMarkCircleIcon', name: 'question-mark-circle', label: 'Help' },
  { componentName: 'AcademicCapIcon', name: 'academic-cap', label: 'Graduation cap' },
];

export interface HubWithStepsAndLinks extends Hub {
  hubSteps: Array<HubStep>;
  hubResourceLinks: Array<HubResourceLink>;
}

export interface AccountButtonColors {
  text: string;
  background: string;
}

export interface IconProps extends React.ComponentProps<'svg'> {
  className?: string;
  size?: number | string;
}

export enum HubSubmissionEventType {
  FORM_SUBMIT_BUTTON_CLICK = 'form_submit_button_click',
  HUB_X_CLOSE = 'hub_x_close',
  HUB_MEDALLION_CLICK = 'hub_medallion_click',
  BACKDROP_CLICK = 'backdrop_click',
  FORM_COMPONENT_UNMOUNT = 'form_component_unmount', // e.g. happens when changing steps
  ESCAPE_PRESS = 'escape_press',
  TAB_OR_WINDOW_CLOSE_OR_REFRESH = 'tab_or_window_close_or_refresh',
  HUB_OPT_OUT = 'hub_opt_out',
}

export enum AhaWindowMessageTypes {
  DLP_VISIT = 'DLP_VISIT',
  DLP_SUBMISSION = 'DLP_SUBMISSION',
  STOP_GENERATING = 'STOP_GENERATING',
}

export enum NewHubMetricTypesEndpoints {
  FORM_SUBMIT = 'form-submission', // No longer used, but kept for convenience for when we add a new metric
  STEP_IMPRESSION = 'step-impression',
  STEP_VIEW = 'step-view',
  HUB_VISIT = 'hub-visit',
}

export enum FormSubmissionsTableType {
  COMPLETE_SUBMISSIONS = 'complete_submissions',
  PARTIAL_SUBMISSIONS = 'partial_submissions',
}

export enum ExportMethodTypes {
  EMAIL = 'email',
  DOWNLOAD = 'download',
}

export enum ExportTimeframeTypes {
  ALL_TIME = 'all_time',
  SINCE_LAST_EXPORT = 'since_last_export',
  CUSTOM_DATE_RANGE = 'custom_date_range',
}

export enum ScriptInstallationStatusTypes {
  CHECKING = 'CHECKING',
  FOUND = 'FOUND',
  ERROR = 'ERROR',
  NOT_FOUND = 'NOT_FOUND',
  NO_HOME_PAGE_URL = 'NO_HOME_PAGE_URL',
}

export enum UIMessageTypes {
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
}

export enum TabNames {
  ALL_ACCOUNTS = 'All Accounts',
  TEMPLATE_LIBRARY = 'Template Library',
  HOME = 'Home',
  SMART_FORMS = 'AI Forms',
  HUBS = 'AI Hub',
  ANALYTICS = 'Analytics',
  FEATURE_FLAGS = 'Feature Flags',
}

export enum SidebarHeader {
  ADMINISTRATOR = 'Administrator',
  MY_ACCOUNT = 'My Account',
}

export enum ReadinessStatus {
  BETA = 'Beta',
  PILOT = 'Pilot',
  GA = 'GA',
}

export interface SidebarNavItem {
  id: string;
  name: TabNames;
  icon: keyof typeof iconMap;
  href: string;
  current: HTMLInputElement | null;
  badgeText?: string;
  subItems?: SidebarNavItem[];
}

export interface SidebarNavSubItem {
  id: string;
  name: TabNames;
  href: string;
  current: HTMLInputElement | null;
  tag?: ReadinessStatus;
  icon: keyof typeof iconMap;
}
export interface ParentSidebarNavItem extends SidebarNavItem {
  subItems?: SidebarNavSubItem[];
}

export type AhaSearchItem = {
  url: string;
  title: string;
  description: string;
  isTopicCard?: boolean;
}

export interface AhaMessageEmbeddedSmartFormProps {
  account: Account;
  smartFormVariant: SmartFormVariant;
  variantTemplate: any;
  leadProfileFormData: GeneralHash;
  isPreview: boolean;
  trackingId: string;
}

export type AhaMessageType = {
  key: string;
  id?: string;
  content?: string;
  role: AhaSenders;
  index: number;
  sources?: Array<string>;
  searchResults?: Array<AhaSearchItem>;
  isTyping?: boolean;
  dlpProps?: AhaMessageEmbeddedSmartFormProps;
  contentCardProps?: ContentCardConfig;
  responseTime?: number;
  knowsAnswer?: boolean;
  isDlpGoingToPop?: boolean;
  error?: string;
  backupEngineType?: string;
  userFollowUpQuestions?: Array<string>;
  aiSearchThreadId?: string;
}

export type AiSearchThread = {
  id: string;
  created_at: string;
  messages: AhaMessageType[];
};

export type AiSearchThreadsResponse = {
  success: boolean;
  threads: AiSearchThread[];
  pagination?: {
    total_pages: number;
    current_page: number;
    total_count: number;
  };
  message?: string;
};

export enum AhaLoadingState {
  INTERPRETING = 'interpreting',
  SEARCHING = 'searching',
  GATHERING = 'gathering',
  UNDERSTANDING = 'understanding',
  CLARIFYING = 'clarifying',
  PERSONALIZING = 'personalizing',
  COMPLETE = 'complete',
}

export type AhaSearchStateObject = {
  status: AhaLoadingState,
  content?: string;
}

export type AhaSearchAssumption = {
  id: string;
  text: string;
  tag: string;
}

export enum AhaSenders {
  USER = 'user',
  HAL = 'assistant',
}

export enum AiSeoFeedbackSource {
  INTERNAL = 'internal',
  AUDIT = 'audit',
  STAKEHOLDER = 'stakeholder',
}

// Mirrors AiSeoFeedback::VERDICTS; stored polarity-normalized (pass = good)
// regardless of how the question is phrased in the UI.
export enum AiSeoVerdict {
  PASS = 'pass',
  FAIL = 'fail',
  NOT_APPLICABLE = 'not_applicable',
}

// Mirrors AiSeoFeedback::VERDICT_DIMENSIONS (count-von-count evaluator keys).
export enum AiSeoVerdictDimension {
  GROUNDEDNESS_CENTRAL = 'groundedness_central',
  GROUNDEDNESS_ANY = 'groundedness_any',
  COMPLETENESS = 'completeness',
  FRESHNESS = 'freshness',
  HELPFULNESS = 'helpfulness',
  ON_TOPIC = 'on_topic',
}

export type AiSeoVerdicts = Partial<Record<AiSeoVerdictDimension, AiSeoVerdict>>;

export enum VerdictHelpTextDisplay {
  INLINE = 'inline',
  TOOLTIP = 'tooltip',
}

export type AiSeoFeedback = {
  id?: string;
  rating?: number;
  feedback?: string;
  preferredResponse?: string;
  userEmail?: string;
  updatedAt?: string;
  source?: AiSeoFeedbackSource;
  stakeholderName?: string;
  stakeholderComment?: string;
  verdicts?: AiSeoVerdicts | null;
};

export interface LeadProfileInfo {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  createdAt: string;
  segmentName?: string;
  segmentId?: string;
}

export enum AhaMessageSearchTypes {
  REGULAR = 'regular',
  HUB = 'hub',
  DYNAMIC_QUESTION = 'dynamic_question',
  TOP_PARAGRAPH = 'top_paragraph',
  AUDIT = 'audit',
  PHONE = 'phone',
  SMS = 'sms',
  EMAIL = 'email',
  DRIP_CAMPAIGN = 'drip_campaign',
}

export type StudentConversationalSearchQuery = {
  role: AhaSenders;
  content: string;
  aiCitations: Array<string>;
  suggestedUrls: Array<string>;
  userQuery: string;
  queryId: string;
  responseId: string;
  knowsAnswer: boolean;
  ahaSessionId: string;
  responseTime: number;
  categoryName: string;
  queryDate: string;
  searchType?: AhaMessageSearchTypes;
  feedbackType?: FeedbackTypes;
  feedbackComment?: string;
  debugLogS3Url?: string;
  aiSeoFeedback?: AiSeoFeedback | null;
  leadProfile?: LeadProfileInfo | null;
  isPreview?: boolean;
  isAudit?: boolean;
}

export type SemanticSearchResult = {
  id: string;
  text: string;
  similarity_score: number;
  source: string;
  tokens: number;
};

export enum HaldaMessageType {
  STUDENT_PREVIEW_BLOCKED = 'halda:student-preview-blocked',
  VARIANT_SWAPPED = 'variant-swapped',
  REPLACE_WIDGET_WITH_AHA = 'replace-widget-with-aha',
  UPDATE_AHA_QUERY = 'update-aha-query',
  SMART_FORM_ERROR = 'smart-form-error',
  DISPLAY_RESPONSE_PAGE = 'display-response-page',
  SUBMIT = 'submit',
  SECONDARY_ACTION = 'secondary-action',
  CLOSE = 'close',
  CLOSE_HUB = 'close-hub',
  HUB_READY = 'hub-ready',
  OPEN_HUB = 'open-hub',
  TARGET_URLS_UPDATED_IN_BUILDER = 'TARGET_URLS_UPDATED_IN_BUILDER',
  HUB_TAB = 'hub-tab',
  HUB_SHIFT_TAB = 'hub-shift-tab',
  HUB_OPT_OUT = 'hub-opt-out',
  HUB_FINAL_CTA_CLICK = 'hub-final-cta-click',
  HUB_QUICK_LINK_CLICK = 'hub-quick-links-click',
  HUB_SUBMISSION = 'hubSubmitFormData',
  OPEN_HUB_CARD = 'hub-open-card',
  MEDALLION_CLICKED = 'medallion-clicked',
  RECORD_LOG_MESSAGE = 'record-log-message',
  CLICK_TO_POP_OPEN_AHA_IN_HUB = 'click-to-pop-open-aha-in-hub',
  RESPONSE_PAGE_HEIGHT = 'response-page-height',
  CONTENT_CARD_MODAL_CLOSE = 'CONTENT_CARD_MODAL_CLOSE',
}

export enum AutoGenerationType {
  GENERATE = 'generate',
  REGENERATE = 'regenerate',
  SHORTEN = 'shorten',
  LENGTHEN = 'lengthen',
}

export enum HaldaWidgetMode {
  debug = 'debug',
}

export interface ResponseStreamData {
  message?: string;
  finishReason?: string;
  debugInfo?: string;
  messageId: string;
  urlWithAnswer?: string;
  endpointStatus?: AhaSearchStateObject;
  error?: string;
  eventData?: GeneralHash;
  customInstructions?: string;
  isLinkCorrection?: boolean;
}

export interface User {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  invitedByUserEmail?: string;
  isArchived?: boolean;
  role?: UserRole;
  isHaldaAdmin?: boolean;
  isHaldaDev?: boolean;
  isHaldaProduct?: boolean;
  uiAnimExempt?: boolean;
  uiAnimDisabledPranks?: string[];
  targetedPrankCode?: string | null;
  department?: DepartmentType;
  jobTitle?: string;
  receiveMonthlyLeadDigestEmail: boolean;
  receiveLeadNotificationEmail: boolean;
  receiveLowPromoCodesEmail: boolean;
  receiveProductUpdates: boolean;
  receiveIntegrationIssuesEmail: boolean;
  receiveConversationNotificationEmail: boolean;
  conversationNotificationFrequency: ConversationNotificationFrequency;
  receiveFeedbackNotificationEmail: boolean;
  receiveDripCampaignSendNotifications: boolean;
  dripCampaignSendNotificationTiming: DripCampaignSendNotificationTiming;
  disabledSmartFormNotifications: Array<string>;
  slateGatewayEmailAddress?: string;
  isSidebarExpanded?: boolean;
  isEnrollmentMarketerExpanded?: boolean;
  isStudentRecruiterExpanded?: boolean;
  isAnalyticsExpanded?: boolean;
  isCampaignAgentExpanded?: boolean;
  isAiConfigurationExpanded?: boolean;
  permissions: UserPermission;
}

export interface UserPermission {
  viewHome: boolean;
  viewDynamicLandingPages: boolean;
  editDynamicLandingPages: boolean;
  viewHubs: boolean;
  editHubs: boolean;
  editFields: boolean;
  viewConversationalSearch: boolean;
  editConversationalSearch: boolean;
  viewAnalytics: boolean;
  shareLead: boolean;
  emailLead: boolean;
  viewLeads: boolean;
  viewAdministrator: boolean;
  viewCampaignTracking: boolean;
  editCampaignTracking: boolean;
  editBranding: boolean;
  manageIntegrations: boolean;
  manageUsers: boolean;
  viewFields: boolean;
  archiveLead: boolean;
  viewContentCards: boolean;
  editContentCards: boolean;
  viewContentAnalytics: boolean;
  editContentAnalytics: boolean;
  viewTopicInstructions: boolean;
  editTopicInstructions: boolean;
  ingestLeads: boolean;
  editAiKnowledgeBase: boolean;
  viewPii: boolean;
}

export interface UserFormData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  role: UserRole;
  department: string;
  business_role?: BusinessRoleType;
  job_title: string;
  emailLead: boolean;
  permissions: UserPermission;
  isHaldaSales: boolean;
  hsUserId?: number;
  hsMonthlySalesGoal?: number;
  hsSprintPointsGoal?: number;
}


export enum ConversationNotificationFrequency {
  DAILY = 'DAILY',
  HOURLY = 'HOURLY',
}

export enum DripCampaignSendNotificationTiming {
  MORNING_OF = 'morning_of',
  MORNING_BEFORE = 'morning_before',
}

export interface NotificationSettings {
  receiveMonthlyLeadDigestEmail: boolean;
  receiveLeadNotificationEmail: boolean;
  receiveLowPromoCodesEmail: boolean;
  receiveProductUpdates: boolean;
  receiveIntegrationIssuesEmail: boolean;
  receiveConversationNotificationEmail: boolean;
  conversationNotificationFrequency: ConversationNotificationFrequency;
  receiveFeedbackNotificationEmail: boolean;
  disabledSmartFormNotifications: Array<string>;
  globallyUnsubscribed: boolean;
}

export enum FreneticWidgetTypes {
  JAXED = 'JAXED',
  HELLO_WORLD = 'HELLO_WORLD',
  PHONE_CALL = 'PHONE_CALL',
}

export interface AhaConfig {
  id: string;
  isPublished: boolean;
  initialSearchSourceUrl: string;
  excludedUrlPaths: string;
  previewUrl: string;
  searchSuggestions: Array<AhaSearchSuggestion>;
  includedTargetUrls: Array<TargetUrl>;
  excludedTargetUrls: Array<TargetUrl>;
  languageTranslationDetection: boolean;
  targetType: TargetType;
  isLightboxEnabled: boolean;
  customInstructions: string;
  cta: string;
  isDisclaimerTextEnabled: boolean;
  disclaimerText: string;
  accountNameOverride?: string;
  sendImmediatelyThreshold: number;
  doNotSendIfWorseThanThreshold: number;
  isRagieDocumentUploadEnabled?: boolean;
  isGeminiEngineEnabled?: boolean;
  isGeminiConfigurationEnabled?: boolean;
  isGeminiReadyForActivation?: boolean;
  vertexIncludedPaths?: string[];
  vertexWebsiteDataStoreId?: string;
  vertexDocumentDataStoreId?: string;
  vertexSearchAppId?: string;
  vertexIncludeSubdomains?: string;
  vertexSitemaps?: string[];
}

export interface AhaUpdateParams {
  searchSourceUrl?: string;
  excludedUrlPaths?: string;
  vertexIncludedPaths?: string[];
  vertexIncludeSubdomains?: string;
  isPublished?: boolean;
  targetType?: TargetType;
  isDisclaimerTextEnabled?: boolean;
  disclaimerText?: string;
  successMessage: string;
  errorMessage: string;
  onSuccess?: (aha: AhaConfig) => void;
}

export interface AhaSearchProps {
  host: string;
  ahaPath: string;
  account: Account;
  trackId: string;
  isPreview: boolean;
  isClickToPop: boolean;
  widgetsZIndex: number;
  accountButtonColors?: AccountButtonColors;
  haldaWidgetMode: HaldaWidgetMode;
  isLightboxEnabled: boolean;
  initialQuery?: string;
  googleAnalyticsConfiguration: GoogleAnalyticsIntegration;
  refererVariantId?: string;
  refererSubmissionId?: string;
}

export interface AhaContainerChildProps {
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  formRef: React.RefObject<HTMLFormElement | null>;
  handleChooseSuggestion: (suggestion: string) => void;
  messages: Array<AhaMessageType>;
  sendMessage: (query: string) => boolean | Promise<boolean>;
  isLoading: boolean;
  loadingState: AhaSearchStateObject;
  isAnyDlpPresentOrGoingToBePresent: boolean;
  isAMessageCurrentlyLoading: boolean;
  stopGeneratingResponse: (messageId: string) => void;
  searchResultsAreOpen: boolean;
  setSearchResultsAreOpen: React.Dispatch<React.SetStateAction<boolean>>;
  searchResults: Array<AhaSearchItem>;
  setSearchResults: React.Dispatch<React.SetStateAction<Array<AhaSearchItem>>>;
  ahaSearchSuggestionsRefs?: Array<React.RefObject<HTMLButtonElement | null>>;
}

export interface AhaSearchInterface {
  init: () => void;
  openAha: () => void;
  expungeAha: () => void;
  setShouldShowMedallionOnClose: (shouldShow: boolean) => void;
  updateInitialQueryAndOpen: (initialQuery: string, refererVariantId?: string, refererSubmissionId?: string) => void;
}

export interface HubProps {
  trackId: string;
  accountId: string;
  leadProfileFormData: any;
  isPreview: boolean;
  hubId: string;
  host: string;
  accountButtonColors?: AccountButtonColors;
  medallionMsgList: Array<{ text: string, index: number }>;
  isHubOpenInitially: boolean;
  widgetsZIndex: number;
  ahaProps: AhaSearchProps;
}

export interface FreneticWidgetInterface {
  init: () => void;
}

export interface ThumbsIconProps {
  height?: number;
  width?: number;
}

export enum FeedbackTypes {
  HELPFUL = 'HELPFUL',
  NOT_HELPFUL = 'NOT_HELPFUL',
}

export enum OffSiteFeedbackSubjects {
  PHONE_CALL = 'PHONE_CALL',
  SMS_MESSAGE = 'SMS_MESSAGE',
  WHATSAPP_MESSAGE = 'WHATSAPP_MESSAGE',
  EMAIL = 'EMAIL',
}

export enum OffSiteCommsLocations {
  RESPONSE_PAGE = 'RESPONSE_PAGE',
  AI_HUB = 'AI_HUB',
}

export const feedbackTypeNameLookup = {
  [FeedbackTypes.HELPFUL]: 'Helpful',
  [FeedbackTypes.NOT_HELPFUL]: 'Not Helpful',
};

export interface WidgetFeedback {
  id: string,
  type: FeedbackTypes,
  feedbackText: string,
  smartFormName: string,
  smartFormLink: string,
  firstName: string,
  lastName: string,
  createdAt: Date;
}

export enum FeedbackLocations {
  RESPONSE_PAGE = 'RESPONSE_PAGE',
  CARD = 'CARD',
}

export enum AhaFeedbackCategory {
  POOR_SUMMARY = 'Poor Summary',
  INNACURATE = 'Innacurate Information',
  NOT_HELPFUL = 'Not Helpful',
  NONE = 'No Feedback Category Provided',
}

export interface SendAhaFeedbackParams {
  feedbackCategory: AhaFeedbackCategory;
  feedbackComment: string;
}

export enum CountryCode {
  AC = 'AC', AD = 'AD', AE = 'AE', AF = 'AF', AG = 'AG', AI = 'AI', AL = 'AL', AM = 'AM', AO = 'AO',
  AR = 'AR', AS = 'AS', AT = 'AT', AU = 'AU', AW = 'AW', AX = 'AX', AZ = 'AZ', BA = 'BA', BB = 'BB',
  BD = 'BD', BE = 'BE', BF = 'BF', BG = 'BG', BH = 'BH', BI = 'BI', BJ = 'BJ', BL = 'BL', BM = 'BM',
  BN = 'BN', BO = 'BO', BQ = 'BQ', BR = 'BR', BS = 'BS', BT = 'BT', BW = 'BW', BY = 'BY', BZ = 'BZ',
  CA = 'CA', CC = 'CC', CD = 'CD', CF = 'CF', CG = 'CG', CH = 'CH', CI = 'CI', CK = 'CK', CL = 'CL',
  CM = 'CM', CN = 'CN', CO = 'CO', CR = 'CR', CU = 'CU', CV = 'CV', CW = 'CW', CX = 'CX', CY = 'CY',
  CZ = 'CZ', DE = 'DE', DJ = 'DJ', DK = 'DK', DM = 'DM', DO = 'DO', DZ = 'DZ', EC = 'EC', EE = 'EE',
  EG = 'EG', EH = 'EH', ER = 'ER', ES = 'ES', ET = 'ET', FI = 'FI', FJ = 'FJ', FK = 'FK', FM = 'FM',
  FO = 'FO', FR = 'FR', GA = 'GA', GB = 'GB', GD = 'GD', GE = 'GE', GF = 'GF', GG = 'GG', GH = 'GH',
  GI = 'GI', GL = 'GL', GM = 'GM', GN = 'GN', GP = 'GP', GQ = 'GQ', GR = 'GR', GT = 'GT', GU = 'GU',
  GW = 'GW', GY = 'GY', HK = 'HK', HN = 'HN', HR = 'HR', HT = 'HT', HU = 'HU', ID = 'ID', IE = 'IE',
  IL = 'IL', IM = 'IM', IN = 'IN', IO = 'IO', IQ = 'IQ', IR = 'IR', IS = 'IS', IT = 'IT', JE = 'JE',
  JM = 'JM', JO = 'JO', JP = 'JP', KE = 'KE', KG = 'KG', KH = 'KH', KI = 'KI', KM = 'KM', KN = 'KN',
  KP = 'KP', KR = 'KR', KW = 'KW', KY = 'KY', KZ = 'KZ', LA = 'LA', LB = 'LB', LC = 'LC', LI = 'LI',
  LK = 'LK', LR = 'LR', LS = 'LS', LT = 'LT', LU = 'LU', LV = 'LV', LY = 'LY', MA = 'MA', MC = 'MC',
  MD = 'MD', ME = 'ME', MF = 'MF', MG = 'MG', MH = 'MH', MK = 'MK', ML = 'ML', MM = 'MM', MN = 'MN',
  MO = 'MO', MP = 'MP', MQ = 'MQ', MR = 'MR', MS = 'MS', MT = 'MT', MU = 'MU', MV = 'MV', MW = 'MW',
  MX = 'MX', MY = 'MY', MZ = 'MZ', NA = 'NA', NC = 'NC', NE = 'NE', NF = 'NF', NG = 'NG', NI = 'NI',
  NL = 'NL', NO = 'NO', NP = 'NP', NR = 'NR', NU = 'NU', NZ = 'NZ', OM = 'OM', PA = 'PA', PE = 'PE',
  PF = 'PF', PG = 'PG', PH = 'PH', PK = 'PK', PL = 'PL', PM = 'PM', PR = 'PR', PS = 'PS', PT = 'PT',
  PW = 'PW', PY = 'PY', QA = 'QA', RE = 'RE', RO = 'RO', RS = 'RS', RU = 'RU', RW = 'RW', SA = 'SA',
  SB = 'SB', SC = 'SC', SD = 'SD', SE = 'SE', SG = 'SG', SH = 'SH', SI = 'SI', SJ = 'SJ', SK = 'SK',
  SL = 'SL', SM = 'SM', SN = 'SN', SO = 'SO', SR = 'SR', SS = 'SS', ST = 'ST', SV = 'SV', SX = 'SX',
  SY = 'SY', SZ = 'SZ', TA = 'TA', TC = 'TC', TD = 'TD', TG = 'TG', TH = 'TH', TJ = 'TJ', TK = 'TK',
  TL = 'TL', TM = 'TM', TN = 'TN', TO = 'TO', TR = 'TR', TT = 'TT', TV = 'TV', TW = 'TW', TZ = 'TZ',
  UA = 'UA', UG = 'UG', US = 'US', UY = 'UY', UZ = 'UZ', VA = 'VA', VC = 'VC', VE = 'VE', VG = 'VG',
  VI = 'VI', VN = 'VN', VU = 'VU', WF = 'WF', WS = 'WS', XK = 'XK', YE = 'YE', YT = 'YT', ZA = 'ZA',
  ZM = 'ZM', ZW = 'ZW',
}

export enum OverlayType {
  MODAL = 'Modal',
  MODAL_MEDALLION = 'Modal Medallion',
  SLIDE_OUT = 'Slide Out',
  EMBEDDED = 'Embedded',
}

export enum HaldaChevronIconDataValues {
  UP = 'M5 15l7-7 7 7',
  DOWN = 'M19 9l-7 7-7-7',
  LEFT = 'M15 19l-7-7 7-7',
  RIGHT = 'M9 5l7 7-7 7',
}

export enum PublishUnpublishWarningStages {
  NONE = 'NONE',
  INCOMPLETE_FIELDS_CHECK = 'INCOMPLETE_FIELDS_CHECK',
  INTEGRATIONS_CHECK = 'INTEGRATIONS_CHECK',
  AB_TEST_CHECK = 'AB_TEST_CHECK',
}

export enum AppAlertTypes {
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  INFO = 'INFO',
}

export type ShowAlertFunctionType = (
  type: AppAlertTypes,
  message: string,
  title?: string,
  fadeOutDelay?: number,
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>
) => void;

export type UserPermissionsItem = {
  name: string;
  formInputName: string;
  children: { name: string; formInputName: string }[];
};

export enum CheckboxState {
  Checked = 'checked',
  Unchecked = 'unchecked',
  Indeterminate = 'indeterminate'
}

export enum HalNotificationName {
  URL_CATEGORIZATION = 'url_categorization',
  AUTOGENERATE_EQUATION = 'autogenerate_equation',
  AUTOGENERATE_RESPONSE = 'autogenerate_response',
  CONVERSATIONAL_SEARCH = 'conversational_search',
  FEEDBACK_NOTIFICATION_SUBSCRIBE = 'feedback_notification_subscribe',
}

export const InAppFeedbackPrompt = {
  url_categorization: 'I generated these categories to organize all your URLs.\nHow well did I categorize them?',
};

export interface PaginationProps<T> {
  fetchItems: (pageNumber: number, onReceiveItems: (items: Array<T>) => void) => void;
  fetchCount: (onReceiveCount: (count: number) => void) => void;
  totalItemCount: number;
  setTotalItemCount: Dispatch<SetStateAction<number>>;
  setCurrentPageItems: Dispatch<SetStateAction<Array<T>>>;
  currentPageItems?: Array<T>;
  startingPage?: number;
  rowsPerPage?: number;
  startDate?: string;
  endDate?: string;
  returnToStartingPageTrigger?: any;
  setParentPageNumber?: Dispatch<SetStateAction<number>>;
}



export enum EmailStatus {
  PENDING = 'pending',
  SENT = 'sent',
  OPENED = 'opened',
  CLICKED = 'clicked',
  BOUNCED = 'bounced',
  OPTED_OUT = 'opted_out',
}

export enum EmailMetricType {
  PAGE_VIEWS = 'page_views',
  HUB_ENGAGEMENTS = 'hub_engagements',
}

export const getEmailMetricTypeDisplayText = (metricType: EmailMetricType): string => {
  switch (metricType) {
    case EmailMetricType.PAGE_VIEWS:
      return 'Page Views';
    case EmailMetricType.HUB_ENGAGEMENTS:
      return 'Hub Engagements';
  }
};

export const getEmailStatusDisplayText = (status: EmailStatus): string => {
  switch (status) {
    case EmailStatus.OPTED_OUT:
      return 'Opted Out';
    case EmailStatus.PENDING:
      return 'Pending';
    case EmailStatus.SENT:
      return 'Sent';
    case EmailStatus.OPENED:
      return 'Opened';
    case EmailStatus.CLICKED:
      return 'Clicked';
    case EmailStatus.BOUNCED:
      return 'Bounced';
  }
};

export interface AiEmailDetail {
  id: string;
  sentDate: string;
  recipient: string;
  trigger: string;
  status: EmailStatus;
  subject: string;
  body: string;
  preheader?: string;
}

export interface EmailDetailsResponse {
  success: boolean;
  emails: AiEmailDetail[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
}

export interface EmailTimelineEvent {
  timestamp: string | null;
  count: number;
}

export interface EmailTimelineEvents {
  sent: EmailTimelineEvent | null;
  opened: EmailTimelineEvent | null;
  clicked: EmailTimelineEvent | null;
  pageViews: EmailTimelineEvent | null;
  hubEngagements: EmailTimelineEvent | null;
}

export interface IndividualEmailMetrics {
  id: string;
  fromAddress: string;
  recipientAddress: string;
  subject: string;
  body: string;
  preheader?: string;
  timelineEvents: EmailTimelineEvents;
}

export type HamburgerMenuItem = {
  id: string;
  name: string;
  href: string;
  dataMethod: string;
  icon: string;
  showDividerAbove: boolean;
};

export interface FieldOption {
  uid: string;
  name: string;
  value: string;
  response?: string;
  qualifiedType?: string;
  description?: string;
  metadata?: Record<string, string>;
  sortOrder?: number;
  sourceUrl?: string;
  formCount?: number;
  smartForms?: Array<{ name: string; editUrl: string }>;
}

export interface FieldRelatedItem {
  id: string;
  name: string;
  questionType?: QuestionType;
  dlpName: string;
  variantName: string;
  isPublished: boolean;
  editLink: string;
  dlpId: string;
}

export interface Field {
  name: string;
  value: string,
  fieldUid: string;
  fieldType: FieldType;
  isSystemField?: boolean;
  fieldOptions?: Array<FieldOption>;
  createdAt: string;
  smartFormNames: Array<string>;
  smartFormIds: Array<string>;
  relatedQuestions: Array<FieldRelatedItem>
  relatedHiddenAnswers: Array<FieldRelatedItem>;
  response?: string;
  responseType?: ResponseType;
  qualifiedResponse?: string;
  unqualifiedResponse?: string;
  config?: FieldConfig;
}

export interface FieldForIntegrations {
  name: string;
  value: string,
  fieldUid: string;
  fieldType: FieldType;
  fieldOptions?: Array<FieldOption>;
}

export interface FieldOptionForLeadCsvMapping {
  id: string;
  name: string;
  value: string;
  isNew?: boolean;
  aiAlias?: string;
}

export interface FieldForLeadCsvMapping {
  id: string;
  name: string | null;
  value: string | null,
  fieldType: FieldType | NonQuestionFieldType;
  dataCategory?: FieldDataCategory | null;
  fieldOptions?: Array<FieldOptionForLeadCsvMapping>;
  isNew?: boolean;
  aiAlias?: string;
  isBucketed?: boolean;
}

export const FIELD_DATA_CATEGORIES = {
  geographic: 'geographic',
  temporal: 'temporal',
  behavioral: 'behavioral',
  interest_based: 'interest_based',
  demographic: 'demographic',
  academic: 'academic',
  administrative: 'administrative',
} as const;

export type FieldDataCategory = typeof FIELD_DATA_CATEGORIES[keyof typeof FIELD_DATA_CATEGORIES];

export const FIELD_DATA_CATEGORY_LABELS: Record<FieldDataCategory, string> = {
  geographic: 'Geographic',
  temporal: 'Temporal',
  behavioral: 'Behavioral',
  interest_based: 'Interest-Based',
  demographic: 'Demographic',
  academic: 'Academic',
  administrative: 'Administrative',
};

export interface SftpFieldMapping {
  fieldUid: string;
  sftpConnectionId: string;
  smartFormId: string;
  csvName: string;
}

export enum AttributionType {
  FIRST_TOUCH = 'firstTouch',
  LAST_TOUCH = 'lastTouch',
}

export const attributionTypeToStringMap: { [attributionType in AttributionType]: string } = {
  [AttributionType.FIRST_TOUCH]: 'First Touch',
  [AttributionType.LAST_TOUCH]: 'Last Touch',
};

type QuestionTypeToFieldTypeMap = { [questionType in QuestionType]: FieldType | undefined }

export const questionTypeToFieldTypeMap: QuestionTypeToFieldTypeMap = {
  [QuestionType.DROPDOWN]: FieldType.LISTLIKE,
  [QuestionType.RADIO_BUTTON]: FieldType.LISTLIKE,
  [QuestionType.LIST]: FieldType.LISTLIKE,
  [QuestionType.CHECKBOX]: FieldType.MULTISELECT,
  [QuestionType.PHONE_NUMBER]: FieldType.PHONE_NUMBER,
  [QuestionType.PROGRAM_START]: FieldType.PROGRAM_START,
  [QuestionType.SHORT_ANSWER]: FieldType.TEXT,
  [QuestionType.FIRST_NAME]: FieldType.TEXT,
  [QuestionType.LAST_NAME]: FieldType.TEXT,
  [QuestionType.HIGH_SCHOOL]: FieldType.TEXT,
  [QuestionType.ZIP_CODE]: FieldType.TEXT,
  [QuestionType.EMAIL_ADDRESS]: FieldType.TEXT,
  [QuestionType.PARAGRAPH]: FieldType.TEXT,
  [QuestionType.SLIDER]: FieldType.NUMBER,
  [QuestionType.DATE_OF_BIRTH]: FieldType.DATE_PICKER,
  [QuestionType.DATE_PICKER]: FieldType.DATE_PICKER,
  [QuestionType.QUESTION_GROUP]: undefined,
  [QuestionType.STATEMENT]: undefined,
};

export type FieldsFormData = {
  id: string;
  name: string;
  value: string;
  fieldType: FieldType;
  fieldOptionsArray?: FieldOption[];
  response?: string;
  responseType?: string;
  qualifiedResponse?: string;
  unqualifiedResponse?: string;
};

export interface AttributionFunnelStageData {
  funnelStageTitle: string;
  value: number;
  smartFormId?: number;
}

export interface AttributionUtmData {
  title: string;
  customName?: string;
  values: Array<AttributionFunnelStageData>;
}

export interface AttributionData {
  utm_campaign: Array<AttributionUtmData>;
  utm_source: Array<AttributionUtmData>;
  utm_term: Array<AttributionUtmData>;
  utm_content: Array<AttributionUtmData>;
  utm_medium: Array<AttributionUtmData>;
  utm_id: Array<AttributionUtmData>;
}

export interface GlobalHandler {
  type: string;
  listener: (e: any) => void;
  element: 'window' | 'document';
}

export interface FieldPickerItem extends PickerItem, Field {
  fieldName?: string;
}

export interface FieldOptionPickerItem extends PickerItem, FieldOption { }

export const UNASSIGNED_SMART_FORM_NAME = 'Unassigned';

export enum DevMenuLocalStorageKeys {
  ACCOUNT = 'account',
  SMART_FORM = 'smartForm',
  VARIANT = 'variant',
  NUM_DAYS = 'numDays',
  NUM_VISITS_PER_DAY = 'numVisitsPerDay',
  JOB_ID = 'jobId',
  PROGRESS_CACHE_KEY = 'progressCacheKey',
}

export interface AdminAccount {
  uid: string;
  name: string;
}

export interface Admin {
  id: string;
  fullName: string;
  assignedAccounts: Array<AdminAccount>;
  email: string;
  phoneNumber: string;
  isArchived?: boolean;
  invitedByUserEmail?: string;
  role?: UserRole;
}

export interface AdminInvite {
  id: string;
  fullName: string;
  assignedAccounts: Array<AdminAccount>;
  email: string;
  phoneNumber: string;
  isArchived?: boolean;
  invitedByUserEmail?: string;
  role?: UserRole;
}

export interface CtaObject {
  resultsPageCtaLink: string;
  resultsPageCtaLinkPath: string;
  resultsPageCtaType: string;
  resultsPageCtaTypePath: string;
}

export const PII_QUESTION_TYPES = [
  QuestionType.FIRST_NAME,
  QuestionType.LAST_NAME,
  QuestionType.EMAIL_ADDRESS,
  QuestionType.PHONE_NUMBER,
  QuestionType.DATE_OF_BIRTH,
  QuestionType.ZIP_CODE,
]

export const AccountsWithBadTrackingIds = [
  'ckvlayc0r006r08uejbbjw1wi', // Lackawanna College
  'cl01ijpch01c4080ga3ii3zjy', // Cottey College
];

export const DEFAULT_NEW_HSA_PATH = '';

export enum EngineerStatus {
  WORKING = 'Working',
  VACATION = 'Vacation',
}

export interface Engineer {
  id: string;
  uid: string;
  name: string;
  slackId: string;
  githubUsername: string | null;
  jiraUsername: string | null;
  slackEmoji: string | null;
  status: EngineerStatus;
  squadCrPosition: number;
  teamCrPosition: number;
  crossSquadDebt: number;
  isOnCall: boolean;
  imageUrl: string;
  soundName: string;
  isNoLongerWithUs: boolean;
  startDate: string | null;
  endDate: string | null;
  engineerSquadUid: string | null;
}

export interface ProductManager {
  uid: string;
  name: string;
  imageUrl: string | null;
}

export interface EngineerSquad {
  uid: string;
  name: string;
  productManagerUids: string[];
  engineerUids: string[];
}

export interface SmartFormVariantPublishingOptions {
  shouldStartAbTest?: boolean;
  shouldRestartAbTest?: boolean;
  smartFormVariantName?: string;
  shouldSwapOutPublishedVariant?: boolean;
}

export const BUILDER_STEP_WIDTH_KEY = 'builderStepWidth';
export const BUILDER_SIDEBAR_WIDTH_KEY = 'builderSidebarWidth';

export enum MedallionPosition {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
  CENTER = "CENTER",
}

export enum MedallionType {
  CARDS = 'MedallionWithCard',
  CENTERED = 'MedallionCentered',
  ACTION_BAR = 'MedallionActionBar'
}

export type HubSteppable = SmartFormVariant;

export enum HubSteppableType {
  SMART_FORM_VARIANT = 'SmartFormVariant',
  CONTENT_CARD = 'ContentCard',
}

export interface StudentPreview {
  studentName: string;
  exitUrl: string;
  sessionPath: string;
  trackingId: string;
  hasNoHubContent: boolean;
}

export interface NewHubProps {
  account: Account;
  disclosureVersions?: DisclosureVersion[];
  hub: NewHub;
  isPreview: boolean;
  trackingId: string;
  leadProfileFormData: GeneralHash;
  leadProfileId: string;
  isUnitedStatesTraffic: boolean;
  initialActiveStepIdx: number;
  abTestComponents: AbTestComponent[];
  referer: string;
  recaptchaSiteKey: string;
  globalEmbedScriptTag?: string;
  isRecaptchaEnabled: boolean;
  initialAhaQuery?: string;
  studentPreview?: StudentPreview;
}

export enum BuilderRouteTypeUniqueUrlPart {
  BRANDING_AND_DESIGN = '/billboard',
  INTEGRATE = '/integrate',
  INTEGRATE_V2 = '/integrate-v2',
  PUBLISHING = '/publishing',
  HIDDEN_STATIC_ANSWER = '/static',
  RESPONSE_PAGE = '/results-page',
  HUB_CONFIGURATION = '/hub',
  LANDING_PAGE_CONFIGURATION = '/landing-page',
  QUESTIONS = '/questions',
  BEHAVIORAL_SMS = '/behavioral-sms',
}

export interface BehavioralSmsOpener {
  uid: string;
  body: string;
}

export const HUB_MEDALLION_CARDS_DISMISSAL_ANIMATION_DURATION = 0.5;
export const SUBMISSION_ID_KEY = 'Submission Id';

export enum ModalPriority {
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum DisplayField {
  SHOW_IN_HUB = 'showInHub',
  SHOW_AS_MODAL = 'showAsModal',
}

export interface TargetingLocationsData {
  targetingType: TargetingLocationsRules;
  countries?: string[];
  states?: string[];
}

export interface ContentCardConfig {
  id: string;
  name?: string;
  recommendationLogic?: string;
  heading?: string;
  subheading?: string;
  buttonText?: string;
  buttonLink?: string;
  showPoweredByLogo: boolean;
  isPublished: boolean;
  imageSrcUrl?: string;
  logoSrcUrl?: string;
  imageAltText?: string;
  startDate?: string;
  endDate?: string;
  deactivateAfterView: boolean;
  targetType?: TargetType;
  includedUrls?: TargetUrl[];
  excludedUrls?: TargetUrl[];
  showInHub: boolean;
  showAsModal: boolean;
  modalPriority: ModalPriority;
  overrideAiForms: boolean;
  triggerType: TriggerType;
  triggerValue?: number;
  frequencyType: FrequencyType;
  targetCountries?: TargetingLocationsData;
  targetStates?: TargetingLocationsData;
  targetUtms?: { [key: string]: string } | null;
}

export interface TopicCardSource {
  id: string;
  sourcePageUrl: string;
  isScraped?: boolean;
}

export interface TopicCard {
  id: string;
  topicLabel: string;
  classificationDescription: string;
  instructions: string;
  sourcePages: TopicCardSource[];
  creatorName: string;
  updatedAt: string;
  scrapedSourceUrls?: string[];
  instructionType: TopicInstructionType;
  isVerbatim: boolean;
  isLimitedToGivenUrls: boolean;
  excludedSegmentIds?: string[];
  isTrainingGenerated: boolean;
  usageCount?: number;
  usagePercentage?: number;
  isArchived?: boolean;
  versionNumber: number;
  expirationDate: string;
  ragieSyncStatus: string;
  publishedAt?: string | null;
  archivedAt?: string | null;
  archiveReason?: string | null;
  topicCardUrl?: string;
}

export type TopicCardStatusType = 'draft' | 'published' | 'scheduled' | 'archived' | 'expired';

export enum ArchiveReason {
  MANUAL = 'manual',
  EXPIRED = 'expired',
  SUPERSEDED = 'superseded',
}

export const getTopicCardStatus = (topicCard: TopicCard): TopicCardStatusType => {
  if (topicCard.archivedAt) {
    if (topicCard.archiveReason === ArchiveReason.EXPIRED) {
      return 'expired';
    }
    return 'archived';
  } else if (topicCard.publishedAt) {
    const publishedDate = new Date(topicCard.publishedAt);
    if (publishedDate > new Date()) {
      return 'scheduled';
    }
    return 'published';
  } else {
    return 'draft';
  }
};

export const getDaysUntilExpiration = (expirationDate: string): number => {
  const expiration = new Date(expirationDate);
  const now = new Date();
  const diffTime = expiration.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const isExpiringSoon = (expirationDate: string): boolean => {
  const daysUntil = getDaysUntilExpiration(expirationDate);
  return daysUntil >= 0 && daysUntil < 7;
};

export const DEFAULT_HUB_STEP_IMAGE = 'https://i1.pickpik.com/photos/289/102/403/various-book-books-college-preview.jpg';

export const PERCENTAGE_BAR_BAD_COLOR = '#FF6B00';
export const PERCENTAGE_BAR_GOOD_COLOR = '#0888e8'; // this is halda-cobalt

export enum PhoneStatus {
  NONE = 'NONE',
  COLLECT_NUMBER_FOR_CALL = 'COLLECT_NUMBER_FOR_CALL',
  COLLECT_NUMBER_FOR_TEXT = 'COLLECT_NUMBER_FOR_TEXT',
  COLLECT_NUMBER_FOR_WHATSAPP = 'COLLECT_NUMBER_FOR_WHATSAPP',
  SENDING_PHONE_CALL = 'SENDING_PHONE_CALL',
  SENDING_TEXT = 'SENDING_TEXT',
  SENDING_WHATSAPP = 'SENDING_WHATSAPP',
  COLLECT_FEEDBACK_FOR_CALL = 'COLLECT_FEEDBACK_FOR_CALL',
  COLLECT_FEEDBACK_FOR_TEXT = 'COLLECT_FEEDBACK_FOR_TEXT',
  COLLECT_FEEDBACK_FOR_WHATSAPP = 'COLLECT_FEEDBACK_FOR_WHATSAPP',
}

export interface SuggestedSmartForm {
  name: string;
  description: string;
}

export const WAIT_TIME_TO_COLLECT_FEEDBACK_AFTER_SENDING_TEXT = 5000;

export enum BackgroundJobStatuses {
  NOT_STARTED = 'NOT_STARTED',
  STILL_LOADING = 'STILL_LOADING',
  JOB_FINISHED_SUCCESSFULLY = 'JOB_FINISHED_SUCCESSFULLY',
  JOB_FAILED = 'JOB_FAILED',
  COULD_NOT_CHECK = 'COULD_NOT_CHECK',
  TERMINATED_AT_BACKOFF_CAP = 'TERMINATED_AT_BACKOFF_CAP',
}

export enum VertexIndexingStatuses {
  SUCCEEDED = 'SUCCEEDED',
  INDEXING = 'INDEXING',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}

export interface BackgroundJobStatusType {
  id?: string;
  message: string;
  status: BackgroundJobStatuses;
  data?: any;
}

export enum BackgroundJobTypes {
  AI_SEARCH_AUDIT_JOB = 'AI_SEARCH_AUDIT_JOB',
  RESEND_LEADS_JOB = 'RESEND_LEADS_JOB',
  MANUAL_FUNNEL_DATA_UPLOAD_JOB = 'MANUAL_FUNNEL_DATA_UPLOAD_JOB',
  PHONE_CALL_JOB = 'PHONE_CALL_JOB',
  AI_GENERATE_SMART_FORM_JOB = 'AI_GENERATE_SMART_FORM_JOB',
  SIMULATION_EXPERIMENT_PROMPT_EXECUTION_JOB = 'SIMULATION_EXPERIMENT_PROMPT_EXECUTION_JOB',
}

export interface AiFormAssistantMessage {
  id: string;
  content: string;
  createdAt: string;
  role: string;
  status?: BackgroundJobStatuses;
};

export type Environment = 'development' | 'production' | 'test';

export interface DevVariable {
  key: string;
  value: string;
  uid: string;
  created_at: string;
  updated_at: string;
}

export interface FilterOption {
  value: string;
  label: string;
  selectedValues?: string[];
}

export interface CategoryData {
  key: string;
  title: string;
  options: FilterOption[];
}

export interface CallDestination {
  id: string;
  name: string;
  phone_number: string;
}

export enum BackendConstantType {
  SMS_GREETING = 'SMS_GREETING',
  SMS_DOES_NOT_KNOW_ANSWER_MESSAGE = 'SMS_DOES_NOT_KNOW_ANSWER_MESSAGE',
  PHONE_GREETING = 'PHONE_GREETING',
  PHONE_SEARCHING_MESSAGE = 'PHONE_SEARCHING_MESSAGE',
  DEFAULT_DISCLAIMER_TEXT = 'DEFAULT_DISCLAIMER_TEXT',
  SYSTEM_WIDE_FIELDS = 'SYSTEM_WIDE_FIELDS',
}

export enum ColumnGroupNames {
  PERSONAL_INFORMATION = 'Personal Information',
  LEAD_INFORMATION = 'Lead Information',
  LEAD_PROFILE_META_DATA = 'Lead Profile Meta Data',
  AI_STUDENT_RECRUITER_META_DATA = 'AI Student Recruiter Meta Data',
  FORM_RESPONSES = 'Form Responses',
  STAGE_TRACKING = 'Stage Tracking'
}

export interface FormInputNameToFieldOptionUidHash {
  [formInputName: string]: string | string[]; // single fieldOptionUid or array for checkboxes
}

export interface AhaMessageLlmQuery {
  id: string;
  promptName: string;
  promptVariables: Record<string, string>;
  response: Record<string, string>;
}

export enum TopicInstructionType {
  CONTENT_GAP = 'CONTENT_GAP',
  URL_SUGGESTION = 'URL_SUGGESTION',
  ENHANCEMENT = 'ENHANCEMENT',
}

export interface TopicInstructionTypeConfig {
  value: TopicInstructionType;
  label: string;
  description: string;
  header: string;
  subheader: string;
  instructionsName: string;
  instructionsDescription: string;
  hasIsVerbatimToggle: boolean;
  hasSourcePages: boolean;
  hasIsLimitedToGivenUrlsToggle: boolean;
}

export const TOPIC_TYPE_CONFIG: { [key in TopicInstructionType]: TopicInstructionTypeConfig } = {
  [TopicInstructionType.CONTENT_GAP]: {
    value: TopicInstructionType.CONTENT_GAP,
    label: 'Content Gap',
    description: 'This topic instruction will answer a question where the answer is not found on your website',
    header: 'Content Gap',
    subheader: 'This topic instruction will answer a question where the answer is not found on your website',
    instructionsName: 'Response',
    instructionsDescription: '',
    hasIsVerbatimToggle: true,
    hasSourcePages: true,
    hasIsLimitedToGivenUrlsToggle: false,
  },
  [TopicInstructionType.URL_SUGGESTION]: {
    value: TopicInstructionType.URL_SUGGESTION,
    label: 'URL Suggestion',
    description: 'Instruct the AI to use one or many URLs when doing a search for this topic',
    header: 'URL Suggestion',
    subheader: 'Instruct the AI to use one or many URLs when doing a search for this topic',
    instructionsName: '',
    instructionsDescription: '',
    hasIsVerbatimToggle: false,
    hasSourcePages: true,
    hasIsLimitedToGivenUrlsToggle: true,
  },
  [TopicInstructionType.ENHANCEMENT]: {
    value: TopicInstructionType.ENHANCEMENT,
    label: 'Enhancement',
    description: 'After the AI finds an answer it will use this topic instruction to augment the answer with details you provide',
    header: 'Enhancement',
    subheader: 'After the AI finds an answer it will use this topic instruction to augment the answer with details you provide',
    instructionsName: 'Enhancement Instructions',
    instructionsDescription: 'Once AI has found an answer, how do you want it to enhance or change the response before returning the answer to the student?',
    hasIsVerbatimToggle: false,
    hasSourcePages: false,
    hasIsLimitedToGivenUrlsToggle: false,
  },
};

export enum LlmProvider {
  OPENAI = 'openai',
  GROQ = 'groq',
  GEMINI = 'gemini',
  CLAUDE = 'claude',
}

export enum LlmModel {
  // OpenAI Models
  GPT_4_1 = 'gpt-4.1',
  GPT_4_1_MINI = 'gpt-4.1-mini',
  GPT_4_1_NANO = 'gpt-4.1-nano',

  // Groq Models
  LLAMA_3_1_8B_INSTANT = 'llama-3.1-8b-instant',

  // Gemini Models
  GEMINI_2_0_FLASH_LITE = 'gemini-2.0-flash-lite',

  // Claude Models
  CLAUDE_3_7_SONNET_LATEST = 'claude-3-7-sonnet-latest',
}

export const LlmProviderReadable = {
  [LlmProvider.OPENAI]: 'OpenAI',
  [LlmProvider.GROQ]: 'Groq',
  [LlmProvider.GEMINI]: 'Gemini',
  [LlmProvider.CLAUDE]: 'Claude',
};

export const LlmProviderColorClasses = {
  [LlmProvider.OPENAI]: 'bg-green-100 text-green-800',
  [LlmProvider.GROQ]: 'bg-blue-100 text-blue-800',
  [LlmProvider.GEMINI]: 'bg-yellow-100 text-yellow-800',
  [LlmProvider.CLAUDE]: 'bg-purple-100 text-purple-800',
};

export const DefaultLlmModels = {
  [LlmProvider.OPENAI]: LlmModel.GPT_4_1_MINI,
  [LlmProvider.GROQ]: LlmModel.LLAMA_3_1_8B_INSTANT,
  [LlmProvider.GEMINI]: LlmModel.GEMINI_2_0_FLASH_LITE,
  [LlmProvider.CLAUDE]: LlmModel.CLAUDE_3_7_SONNET_LATEST,
};
